import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { fromZonedTime } from "date-fns-tz";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { GroupBase, OptionsOrGroups } from "react-select";
import { toast } from "react-toastify";
import {
  useGetAssignedDriverQuery,
  useLazyOrganizationVehiclesQuery,
  useOrganizationGeozonesQuery,
  useOrganizationVehiclesQuery,
  useSingleOrganizationVehicleQuery,
  useVehicleMapDataQuery,
} from "../../api/network/adminApiServices";
import {
  useGetDeviceListingQuery,
  useRequestVideoMutation,
} from "../../api/network/camerasAdminVMApiService";
import { RESPONSE_CODE } from "../../api/network/constants";
import { useLazyGImageryMetaQuery } from "../../api/network/mapApiServices";
import { useLazyGetDeviceGeneralInfoQuery } from "../../api/network/vmApiService";
import {
  setMapStateData,
  setModalsData,
  setUserMapActivity,
  setUserMapRefreshing,
  setUserMapSelection,
  TListingQueryParams,
  TMobileHelpers,
  TModalsState,
  TUserMapActivity,
  TUserMapSelection,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { OrganizationEntityListingPayload } from "../../api/types/Admin";
import {
  CameraEventGetVideoDetailResponseVideos,
  CameraWakeupRequiredInfo,
} from "../../api/types/Camera";
import {
  LegacyGeozoneEntityListingPayload,
  LegacyGeozoneType,
  TLegacyGeozone,
} from "../../api/types/Geozone";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import { TLegacyVehicle } from "../../api/types/Vehicle";
import { VehicleMapDataPoint } from "../../api/types/VehicleMap";
import { VehicleStatusCode } from "../../api/types/VehicleStatusCode";
import dashboardMenuIcons from "../../assets/svg/dashboardMenuIcons";
import svgIcons from "../../assets/svg/svgIcons";
import { mapVehicleIconWrapped } from "../../assets/svg/vehicle-wrapped";
import {
  AdminFormFieldAsyncLazyDropdown,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
  AdminFormFieldSubmit,
  PseudoSelect,
  TSelectboxOption,
} from "../../components/admin/formFields";
import BasicMap, { MapLoadingAnimation } from "../../components/maps/basicMap";
import DirectionsPanel from "../../components/maps/DirectionPanel";
import MapTopRightControls from "../../components/maps/mapTopRightControls";
import RefreshCountdownBar from "../../components/maps/refreshCountdownBar";
import MapVehicleListingColumnItemForBuzzerAlert from "../../components/mapVehicleListingColumnItemForBuzzerAlert";
import SortingFilter from "../../components/sortingFilter";
import { APP_CONFIG, GOOGLE_MAP_MODE_NAMES } from "../../constants/constants";
import { useGMapMode } from "../../hooks/mapGoogle";
import { useMapInit } from "../../hooks/mapInit";
import { useAccountTimezone } from "../../hooks/timezone";
import { routeUrls } from "../../navigation/routeUrls";
import {
  TGeozoneMapDataCircle,
  TGeozoneMapDataPolygon,
  TLatLng,
  TMapLayerOptions,
  TMapState,
  TMapType,
} from "../../types/map";
import { asyncDebounce, hexToRgb } from "../../utils/common";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../utils/dateTime";
import {
  filterOutWhiteGeozones,
  getGeozoneShapeCenter,
} from "../../utils/geozone";
import { useLoggedInUserData } from "../../utils/user";
import { consistentDisplayAssetItem } from "../../utils/vehicle";
import { ACCOUNT_RECORDER_TYPE_VALUES } from "../admin-details/account/constants";
import { mapOperations as mapOperationsCircle } from "../admin-details/geozone/map-circle";
import { mapOperations as mapOperationsPolygon } from "../admin-details/geozone/map-polygon";
import { mapOperations as mapOperationsRoute } from "../admin-details/geozone/map-route";
import { TMapOperationsProps } from "../admin-details/geozone/type";
import {
  geozonePrepareMapState,
  getGeozoneLocs,
} from "../admin-details/geozone/util";
import {
  CAMERA_CAPTURE_MODES,
  CONNECTIVITY_STATE,
} from "../camera-admin/constants";
import { CrashTrackTabParams } from "../crash-track/type";
import {
  getVehicleFieldValueFromUUID,
  getVehicleUUID,
  mapVehicleDisplayTitle,
} from "../map-fleet/common";
import { VEHICLE_MAP_SORTING_OPTIONS } from "../map-fleet/constants";
import {
  mapVehicleIsActive,
  mapVehicleOrientation,
  mapVehicleStateIconSlug as mapVehicleStateIconSlugFromMapOverview,
} from "../map-fleet/map";
import ModalLayerFilters from "../map-fleet/modalLayerFilters";
import { TDataPoint, TMapRefGeozones } from "../map-fleet/type";
import { IGNITION_STATUS_CODES } from "./constants";
import HeaderView from "./headerView";
import { mapOperations, mapUpdatesHandler } from "./map";
import ModalCameraVideo from "./modalCameraVideo";
import ModalEventFilter from "./modalEventFilter";
import { StatusCodeFilter } from "./statusCodeFilter";
import {
  primaryRouteMode,
  overlayRouteModes,
  routeViewIsLocked,
  toggleRouteView,
} from "./multiDay/routeViewModes";
import { useRouteOverlayPaths } from "./multiDay/useRouteOverlayPaths";
import {
  RouteRenderMode,
  TimedPathPoint,
} from "./multiDay/resolveRoutePath";
import { useResolvedRoutePaths } from "../map-multi-vehicle/useResolvedRoutePaths";
import ModalRequestVideo from "./modalRequestVideo";
import ModalVehicleTripEventDetails from "./modalVehicleTripEventDetails";
import ModalVehicleTripFilters, {
  VehicleTripFiltersData,
} from "./modalVehicleTripFilters";
import { clearLegacyMapLayer } from "./multiDay/clearLegacyLayer";
import { CompareRoutesModal } from "./multiDay/CompareRoutesModal";
import { DateSelector } from "./multiDay/DateSelector";
import { todayDateKey } from "./multiDay/dateUtils";
import { isNonTrip } from "./multiDay/tripClassification";
import { TripCompareView } from "./multiDay/TripCompareView";
import { TripDetailPanel } from "./multiDay/TripDetailPanel";
import { tripKeyFor, TripListMultiDay } from "./multiDay/TripListMultiDay";
import { useMultiDateTripData } from "./multiDay/useMultiDateTripData";
import { ReplayControlsPanel } from "./multiDay/ReplayControlsPanel";
import { convertKmphTo, speedUnitSuffix } from "../../utils/distance";
import {
  SPEED_UNIT_VALUES,
  SPEED_UNITS_OPTIONS,
} from "../admin-details/account/constants";
import { TripPointClickSource } from "./multiDay/renderMultiDayRoutes";
import { useMultiDayMapRender } from "./multiDay/useMultiDayMapRender";
import { useMultiDayReplay } from "./multiDay/useMultiDayReplay";
import TripDetails from "./tripDetails";
import { TMapData, TMapRef, TMemoizedMapOperationsProps } from "./type";
import { calculatedTimeFilters } from "./util/timeFilter";
import { inputOnOffValues } from "./values.inputonoff";


export type ScreenMapAssetParams = {
  crashTrackEventData?: CrashTrackTabParams & {};
};

export type ScreenMapAssetBuzzerAlertParams = {
  buzzerAlertTimestamp?: string;
  buzzerAlertId?: string;
  filterTimestampEnd?: string;
  filterTimestampStart?: string;
  vehicleId: string;
};

const RouteCheckboxItem = ({
  id,
  label,
  icon,
  checked,
  disabled = false,
  onChange,
}: {
  id: string;
  label: string;
  icon: string;
  checked: boolean;
  // a view the floor rule holds on - see toggleRouteView
  disabled?: boolean;
  onChange: () => void;
}) => {
  return (
    <label
      className={`flex items-center gap-2 rounded-md py-2 px-2 ${
        disabled
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer hover:bg-accent-blue-paleO66"
      }`}
      htmlFor={id}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className="size-4 rounded-sm"
      />
      <span className="flex-grow text-sm leading-4 text-accent-blue-dark">
        {label}
      </span>
      <span className="flex size-6">
        <img src={icon} alt={"line-style"} className="flex" />
      </span>
    </label>
  );
};

const ScreenMapAsset = ({ crashTrackEventData }: ScreenMapAssetParams) => {
  const {
    buzzerAlertTimestamp,
    buzzerAlertId,
    filterTimestampEnd,
    filterTimestampStart,
    vehicleId: vehicleUUID,
  } = useParams<ScreenMapAssetBuzzerAlertParams>();
  const vehicleId =
    crashTrackEventData?.assetCrashIdAsset ||
    getVehicleFieldValueFromUUID(`${vehicleUUID}`, "deviceid");
  const [showSortingDropdown, setShowSortingDropdown] = useState(false);
  const [selectedSorting, setSelectedSorting] = useState<"desc" | "asc">(
    VEHICLE_MAP_SORTING_OPTIONS[0].value as "desc" | "asc"
  );
  // multi-day keeps its own order, and starts oldest-first: this list is a
  // week read top to bottom, where reading forwards through the days is the
  // natural way in. selectedSorting can't do double duty here - it is also
  // the single-day fetch's sort_time, so flipping it to change the day
  // order would re-query and reorder that view too.
  const [multiDaySorting, setMultiDaySorting] = useState<"desc" | "asc">(
    "asc"
  );
  const { t } = useTranslation("translation", { keyPrefix: "vehicleMap" });
  const { t: tMapFleet } = useTranslation("translation", {
    keyPrefix: "mapFleet",
  });
  const { t: tBuzzerAlert } = useTranslation("translation", {
    keyPrefix: "buzzerAlerts.map",
  });
  const { t: tMain } = useTranslation();
  const navigate = useNavigate();
  const hasCalledLoadComplete = useRef(false);

  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const mapState = useSelector(
    (state: { commonReducer: { mapState: TMapState } }) =>
      state.commonReducer.mapState
  );
  const userMapActivity = useSelector(
    (state: { commonReducer: { userMapActivity: TUserMapActivity } }) =>
      state.commonReducer.userMapActivity
  );
  const userMapRefreshing = useSelector(
    (state: { commonReducer: { userMapRefreshing: boolean } }) =>
      state.commonReducer.userMapRefreshing
  );
  const userMapRefreshingRefAlias = useRef(userMapRefreshing);
  const [hideMapNearRefresh, setHideMapNearRefresh] = useState(false);
  const userMapSelection = useSelector(
    (state: { commonReducer: { userMapSelection: TUserMapSelection } }) =>
      state.commonReducer.userMapSelection
  );
  const { gMapGetMode, gMapSetMode, gMapEnsureAndLaunchStreetView } =
    useGMapMode();
  const mapWrapperRef = React.createRef<HTMLDivElement>();
  const accountData = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );
  // account opt-out: only ignition on/off pinned by default, other events
  // reached by clicking the route
  const mapPinsDisabled = `${accountData?.disablemappins}` === "1";
  const dispatch = useDispatch();

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const mobileHelpers = useSelector(
    (state: { commonReducer: { mobileHelpers: TMobileHelpers } }) =>
      state.commonReducer.mobileHelpers
  );

  // retrieve account timezone
  const { convertToTZForDateFns, tzForDateFns } = useAccountTimezone();
  const thisOrgDriverAllowed = useLoggedInUserData("hasdrivers");
  const showDriver = thisOrgDriverAllowed === "1";

  const { gIdentifyAndSetMapModeToStore } = useMapInit();

  const thisOrgCameraRecorderType = useLoggedInUserData("cameraRecorderType");

  // visionmax request video mutation
  const [requestVideoVM, { isLoading: isLoadingCameraRequestVideoVM }] =
    useRequestVideoMutation();

  const [generalInfoApiTrigger, { isLoading: isLoadingCameraInfo }] =
    useLazyGetDeviceGeneralInfoQuery();

  // fetch single vehicle details
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const {
    data: dataSingleVehicle,
    isFetching: isFetchingSingleVehicle,
    isError: isErrorSingleVehicle,
    error: errorSingleVehicle,
  } = useSingleOrganizationVehicleQuery(
    {
      account_id: thisUserOrganizationId,
      device_id: vehicleId,
    },
    {
      skip: !vehicleId,
      refetchOnMountOrArgChange: true,
    }
  );

  // visionmax - check if device is online
  const { data: dataDeviceListing, isFetching: isFetchingDeviceListing } =
    useGetDeviceListingQuery(
      {
        keyword: dataSingleVehicle?.cameraid, // deviceEventFromState.cameraid,
        model: "",
        status: "",
        offset: 0,
        limit: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      },
      {
        skip:
          thisOrgCameraRecorderType !==
            ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ||
          !dataSingleVehicle?.cameraid,
        refetchOnMountOrArgChange: true, // caching removed to ensure listing always shows correct status
      }
    );

  // fetch single driver details
  const { data: dataAssignedDriver, isError: isErrorAssignedDriver } =
    useGetAssignedDriverQuery(
      {
        account_id: thisUserOrganizationId ?? "",
        device_id: vehicleId,
      },
      { skip: !vehicleId }
    );

  // preparing query params and fetch vehicles for initial dropdown values
  const orgVehiclesQueryParams = React.useRef<OrganizationEntityListingPayload>(
    {
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.DEFAULT_PAGE_SIZE,
      search: "",
      is_active: "active",
    }
  );
  const {
    data: dataOrgVehicles,
    isFetching: isFetchingOrgVehicles,
    // isLoading, not isFetching, gates the map below: this query refetches on
    // every mount, and isFetching is true for those background revalidations
    // too. See the BasicMap render gate for why that mattered.
    isLoading: isLoadingOrgVehicles,
    error,
  } = useOrganizationVehiclesQuery(orgVehiclesQueryParams?.current, {
    skip: !mapState?.mapScriptLoaded,
    refetchOnMountOrArgChange: true,
  });

  // USE OF (GLOBAL) VEHICLE EVENTS API DEPRECATED, INSTEAD WE RELY ON SINGLE VEHICLE LAST EVENT TIMESTAMP
  // const {
  //   data: dataOrgVehiclesEvents,
  //   isFetching: isFetchingOrgVehiclesEvents,
  // } = useOrganizationVehiclesEventsQuery(
  //   {
  //     account_id: `${thisUserOrganizationId}`,
  //   }
  // );

  // fetching crash track data - for intial values of timestamp dropdown
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { crashTrack: crashTrackQueryParams } = listingQueryParams;

  const computeTripFilters = useCallback(
    (unixTimestampFrom: number, unixTimestampTo: number) => {
      return {
        selected_date: new Date(
          dbTimeToJsTime(parseInt(`${unixTimestampFrom}`))
        )
          .toISOString()
          .split("T")[0],
        date_from: formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(parseInt(`${unixTimestampFrom}`)),
          accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
          "yyyy-MM-dd"
        ),
        date_to: formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(parseInt(`${unixTimestampTo}`)),
          accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
          "yyyy-MM-dd"
        ),
        time_from: formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(parseInt(`${unixTimestampFrom}`)),
          accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
          "HH:mm:ss"
        ),
        time_to: formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(parseInt(`${unixTimestampTo}`)),
          accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
          "HH:mm:ss"
        ),
        timezone: accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
        calculatedUnixTimestampFrom: unixTimestampFrom,
        calculatedUnixTimestampTo: unixTimestampTo,
      } satisfies VehicleTripFiltersData;
    },
    [accountData?.timezone]
  );

  const defaultTripFilters = {
    selected_date: "",
    date_from: "",
    date_to: "",
    time_from: "",
    time_to: "",
    timezone: APP_CONFIG.DEFAULT_TIMEZONE,
    calculatedUnixTimestampFrom: null,
    calculatedUnixTimestampTo: null,
  } as VehicleTripFiltersData;
  const [tripFilters, setTripFilters] = useState<VehicleTripFiltersData>(
    !!crashTrackEventData &&
      !!crashTrackQueryParams.start_time &&
      !!crashTrackQueryParams.end_time
      ? computeTripFilters(
          crashTrackQueryParams.start_time,
          crashTrackQueryParams.end_time
        )
      : defaultTripFilters
  );
  const initialRefreshPaused = useRef(true);
  const [refreshPaused, setRefreshPaused] = useState(
    initialRefreshPaused.current
  ); // State to control map refresh from Trip details
  const refreshPausedRefAlias = useRef(refreshPaused); // Ref alias for refreshPaused to be used in map refresh logic without causing re-renders
  const [refreshFrequency, setRefreshFrequency] = useState(
    APP_CONFIG.MAPS.REFRESH_FREQUENCY_OPTIONS[1].value
  ); // "60" // State to control map refresh from Trip details
  const [refreshOptionsExpanded, setRefreshOptionsExpanded] = useState(false); // State to expand/collapse refresh options for map refresh
  const [mapSearchboxOpen, setMapSearchboxOpen] = useState(false);
  // visual progress for refresh countdown (percent: 0-100)
  // visual progress for refresh countdown (percent: 0-100) — use ref to avoid frequent re-renders
  const refreshProgressRef = useRef<number>(100);
  const refreshTotalMsRef = useRef<number>(
    parseInt(refreshFrequency, 10) * 1000
  );
  // nextRefreshAtRef is the single source of truth for the timestamp (ms) when next poll will happen
  const nextRefreshAtRef = useRef<number | null>(null);
  // requestAnimationFrame id for smooth UI updates
  const rafRef = useRef<number | null>(null);

  const [showRouteViewsPopup, setShowRouteViewsPopup] = useState(false);

  // input on/off values moved to a separate file (values.inputonoff.ts) to make prettier ignore the array and prevent formatting issues
  // const inputOnOffValues = [ ... ];

  // RefreshCountdownBar declared outside of component for stable identity

  useEffect(() => {
    // if vehicleId changes (via URL), reset trip filters to prevent extra API call
    setTripFilters(defaultTripFilters);
  }, [vehicleId]);

  useEffect(() => {
    if (!!errorSingleVehicle) {
      if (APP_CONFIG.DEBUG.ALL)
        console.log("errorSingleVehicle", errorSingleVehicle);
    }
    if (
      errorSingleVehicle &&
      (errorSingleVehicle as FetchBaseQueryError)?.status ===
        RESPONSE_CODE.NOT_FOUND
    ) {
      // redirect to fleet map
      navigate(routeUrls.dashboardChildren.mapsChildren.fleet);
    }
  }, [isErrorSingleVehicle, errorSingleVehicle]);

  // set up trip filters (date)
  useEffect(() => {
    if (!!crashTrackEventData) {
      if (!!crashTrackQueryParams.start_time && crashTrackQueryParams.end_time)
        setTripFilters(
          computeTripFilters(
            crashTrackQueryParams.start_time,
            crashTrackQueryParams.end_time
          )
        );
    } else if (!!dataSingleVehicle && !isFetchingSingleVehicle) {
      // update tripFilters with latest data
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "ASSETFILT ====================== lasteventtimestamp",
          dataSingleVehicle?.lasteventtimestamp
        );
      const refDateStringInTimezone = formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(dataSingleVehicle?.lasteventtimestamp || 0),
        accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
        "yyyy-MM-dd"
      );
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "ASSETFILT refDateStringInTimezone",
          refDateStringInTimezone
        );

      const { startOfDayInUTC, endOfDayInUTC } = getStartEndOfDayInUTC(
        refDateStringInTimezone,
        accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE
      );

      const unixTimestampFrom = !!filterTimestampStart
        ? parseInt(`${filterTimestampStart}`)
        : jsTimeToDBTime(startOfDayInUTC.getTime()); // : ((dataSingleVehicle?.lasteventtimestamp || 0) - 3600 * 24 * 1); // 1 day before
      const unixTimestampTo = !!filterTimestampEnd
        ? parseInt(`${filterTimestampEnd}`)
        : jsTimeToDBTime(endOfDayInUTC.getTime()); // dataSingleVehicle?.lasteventtimestamp || 0;
      if (APP_CONFIG.DEBUG.ALL)
        console.log(
          "ASSETFILT >>>>>",
          dbTimeToJsTime(parseInt(`${unixTimestampFrom}`))
        );
      setTripFilters(computeTripFilters(unixTimestampFrom, unixTimestampTo));
    }
  }, [
    dataSingleVehicle,
    isFetchingSingleVehicle,
    crashTrackEventData,
    crashTrackQueryParams,
  ]);

  // multi-day trip selection (up to 7 dates)
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  // Days that have already ended report nothing more, so live updates are
  // off while none of the selected days is today - not merely paused, since
  // there is nothing to resume to. The user's own pause choice is left
  // untouched underneath, so picking today again restores what they had.
  const isPastDatesOnly =
    selectedDates.length > 0 && !selectedDates.includes(todayDateKey());
  const liveUpdatesHeld = refreshPaused || isPastDatesOnly;
  const isEventDeepLink = !!crashTrackEventData || !!buzzerAlertTimestamp;
  const isMultiDay = !isEventDeepLink;

  useEffect(() => {
    if (selectedDates.length === 0 && tripFilters.selected_date) {
      setSelectedDates([tripFilters.selected_date]);
    }
  }, [selectedDates.length, tripFilters.selected_date]);

  useEffect(() => {
    if (!selectedDates[0] || selectedDates[0] === tripFilters.selected_date)
      return;
    setTripFilters(
      calculatedTimeFilters(
        selectedDates[0],
        tripFilters.timezone || APP_CONFIG.DEFAULT_TIMEZONE
      )
    );
  }, [selectedDates[0]]);

  // get selected vehicle data
  const {
    data: dataVehicleMapData,
    isFetching: isFetchingVehicleMapData,
    error: errorVehicleMapData,
    refetch: refetchVehicleMapData,
  } = useVehicleMapDataQuery(
    {
      account_id: `${thisUserOrganizationId}`,
      device_id: `${dataSingleVehicle?.deviceid}`,
      start_timestamp: tripFilters.calculatedUnixTimestampFrom || 0,
      end_timestamp: tripFilters.calculatedUnixTimestampTo || 0,
      sort_time: selectedSorting as "desc" | "asc",
    },
    {
      skip:
        !vehicleId ||
        !dataSingleVehicle ||
        isFetchingSingleVehicle ||
        isMultiDay ||
        // !dataSingleVehicle?.lasteventtimestamp
        // !!!dataOrgVehiclesEvents
        //   ?.find(vehicleItem =>
        //     vehicleItem.deviceid === vehicleId
        //   )?.timestamp
        !tripFilters.calculatedUnixTimestampFrom,
      refetchOnMountOrArgChange: true,
      // pollingInterval: 60 * 1000, // 1 min. polling   // Disabling polling to accomodate polling on trip detail level
    }
  );

  // states for selected trip and filtered / cleaned vehicle map data
  const [selectedTrip, setSelectedTrip] = useState<number | null>(null);
  const [sanitizedVehicleMapData, setSanitizedVehicleMapData] = useState<
    VehicleMapDataPoint[][]
  >([]);

  // Trip numbers belong to trips. Numbering straight off the array index gave
  // one to every heartbeat too, which is what had a day of three runs reading
  // "Trip 2", "Trip 4", "Trip 6". Counted in time order whichever way the
  // list is sorted, so a trip keeps its number when the sort is flipped, and
  // a non-trip gets none at all.
  const singleDayTripNumbers = useMemo(() => {
    const numbers = new Map<number, number>();
    const chronological = sanitizedVehicleMapData.map((_, index) => index);
    if (selectedSorting === "desc") chronological.reverse();
    let counter = 0;
    chronological.forEach((index) => {
      if (isNonTrip(sanitizedVehicleMapData[index])) return;
      counter += 1;
      numbers.set(index, counter);
    });
    return numbers;
  }, [sanitizedVehicleMapData, selectedSorting]);

  const clearSearchRef = useRef<((preserveZoom?: boolean) => void) | null>(
    null
  );

  // states for hovered trip
  const [hoveredTrip, setHoveredTrip] = useState<number | null>(null);

  // states for filtering status codes
  const [filterableStatusCodes, setFilterableStatusCodes] = useState<
    VehicleStatusCode[]
  >([]);
  // null until the Event Filter has been applied, which reads as "pin
  // everything". An empty array is a real selection - every event turned
  // off - and pins nothing.
  const [filteredStatusCodes, setFilteredStatusCodes] =
    useState<StatusCodeFilter>(null);
  // false until the initial data load has populated the status code filters
  const mapTransitionReadyRef = useRef(false);

  // state for selected trip point
  const [selectedTripPoint, setSelectedTripPoint] =
    useState<VehicleMapDataPoint | null>(null);

  // reset map data on fetching vehicle data
  useEffect(() => {
    if (isFetchingSingleVehicle || isFetchingVehicleMapData) {
      setSanitizedVehicleMapData([]);
      // setSelectedTrip(null);
    }
  }, [isFetchingSingleVehicle, isFetchingVehicleMapData]);

  const userMapDefault = useLoggedInUserData("mapDefault");
  const [directionsRenderer, setDirectionsRenderer] = React.useState(null);

  const handleDirectionsChange = (renderer: any) => {
    setDirectionsRenderer(renderer);
  };
  const getUserPreferredMapType = (): TMapType => {
    if (!userMapDefault) {
      return APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType;
    }

    const preference = parseInt(userMapDefault);

    switch (preference) {
      case 0:
        return "roadmap" as TMapType;
      case 1:
        return "satellite" as TMapType;
      default:
        return APP_CONFIG.MAPS.DEFAULTS.TYPE as TMapType;
    }
  };

  /**
   * reset map type on mount
   * -----------------------
   * Before Sep 23 2024, we were trying to set map type to road on unmount,
   * but it was causing issues with mapdata as rest of the map data
   * including mapScriptLoaded was getting erased.
   * This was happening because `mapState` (store data) was unavailable on unmount.
   */
  useEffect(() => {
    const preferredMapType = getUserPreferredMapType();

    dispatch(
      setMapStateData({
        // ...mapState,
        mapType: preferredMapType,
      })
    );

    return () => {
      if (APP_CONFIG.DEBUG.MAPS) {
        console.log(
          "[Map-Asset] Resetting map type to user preference on unmount"
        );
      }
      dispatch(
        setMapStateData({
          // ...mapState,
          mapType: preferredMapType,
        })
      );
      // introduced small delay to ensure updates record on unmount
      // delay removed
      // setTimeout(() => {
      dispatch(
        setUserMapActivity({
          ...userMapActivity,
          assetMap: {
            center: null,
            zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
            mapType: null,
          },
          fleetMap: {
            center: null,
            zoom: APP_CONFIG.MAPS.DEFAULTS.ZOOM,
            mapType: null,
          },
        })
      );
      dispatch(setUserMapRefreshing(false));
      dispatch(
        setUserMapSelection({
          ...userMapSelection,
          assetMap: {
            selectedTrip: null,
            selectedTripPoint: null,
          },
          fleetMap: {
            selectedAssetItem: null,
          },
        })
      );
      // }, 500);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sanitize vehicle map data
  useEffect(() => {
    if (!isMultiDay && !!dataVehicleMapData && !isFetchingVehicleMapData) {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("dataVehicleMapData", dataVehicleMapData);

      setTimeout(() => {
        setHideMapNearRefresh(false);
      }, 2 * 1000); // smaller delay to show a loader near refreshes
      setTimeout(() => {
        dispatch(setUserMapRefreshing(false));
      }, 5 * 1000); // small delay to ensure map updates are done before re-enabling user activity recording

      // trigger loaded callback only once - used with crash track print mode
      if (!hasCalledLoadComplete.current) {
        crashTrackEventData?.onLoadComplete?.();
        hasCalledLoadComplete.current = true;
      }

      const newSanitizedVehicleMapData: VehicleMapDataPoint[][] = Object.keys(
        dataVehicleMapData
      )
        // 5/15/2025 disabled this one line filtering for dataVehicleMapData[key].length > 1
        // .filter((key) => dataVehicleMapData[key].length > 1)

        // disabled filtering for ignition on/off status codes
        // .filter(key => {
        //   const tripPoints = dataVehicleMapData[key];
        //   const tripStartPoint = tripPoints
        //     .find((tripPoint, i) =>
        //       tripPoint.status.statuscode === VehicleStatusCode.STATUS_IGNITION_ON
        //       && !!tripPoint.latitude && !!tripPoint.longitude
        //     );
        //   const tripEndPoint = tripPoints
        //     .find((tripPoint, i) =>
        //       tripPoint.status.statuscode === VehicleStatusCode.STATUS_IGNITION_OFF
        //       && !!tripPoint.latitude && !!tripPoint.longitude
        //     );
        //   if (!tripEndPoint) console.error('============ NO END POINT for trip', key)
        //   return !!tripStartPoint; // && !!tripEndPoint; - not checking for tripEndPoint as open trips also need to be included
        // })

        // if buzzerAlertTimestamp is present, filter out the trip that has the timestamp
        .filter((key) => {
          const tripPoints = dataVehicleMapData[key];
          const buzzerAlertTimestampInt = parseInt(`${buzzerAlertTimestamp}`);
          return !!buzzerAlertTimestamp
            ? !!tripPoints.find(
                (tripPoint) => tripPoint.timestamp === buzzerAlertTimestampInt
              )
            : true;
        })
        .map((key) => {
          const tripPoints = dataVehicleMapData[key];
          return tripPoints.map((tripPoint) => {
            if (APP_CONFIG.DEBUG.MAPS) {
              if (
                [
                  VehicleStatusCode.STATUS_IGNITION_ON,
                  VehicleStatusCode.STATUS_IGNITION_OFF,
                ].includes(tripPoint.statuscode)
              ) {
                console.log(
                  "============ IGN",
                  tripPoint.statuscode === VehicleStatusCode.STATUS_IGNITION_OFF
                    ? "OFF"
                    : "ON",
                  key
                );
              } else {
                // console.log(
                //   tripPoint.statuscode,
                //   getVehicleEventStatusCodeKey(tripPoint),
                //   `${tripPoint.latitude},${tripPoint.longitude}`
                // );
              }
            }
            return {
              ...tripPoint,
              ignitionindex: dataSingleVehicle?.ignitionindex || null,
              resolvedDescription: inputOnOffValues.includes(
                tripPoint.statuscode
              )
                ? (inputStatusCodeDescriptions[tripPoint.statuscode] ??
                  tripPoint.status.description)
                : tripPoint.status.description,
            };
          });
        });
      setSanitizedVehicleMapData((prev) => {
        const isSame =
          JSON.stringify(prev) === JSON.stringify(newSanitizedVehicleMapData);
        return isSame ? prev : newSanitizedVehicleMapData;
      });
      // if buzzerAlertTimestamp is present, select the trip that has the timestamp
      if (!!buzzerAlertTimestamp) {
        const selectedTripIndex = newSanitizedVehicleMapData.findIndex(
          (tripPoints) =>
            tripPoints.some(
              (tripPoint) =>
                tripPoint.timestamp === parseInt(`${buzzerAlertTimestamp}`)
            )
        );
        setSelectedTrip(selectedTripIndex);
        const buzzerEventDataPoint = newSanitizedVehicleMapData[
          selectedTripIndex
        ]?.find(
          (tripPoint) =>
            tripPoint.timestamp === parseInt(`${buzzerAlertTimestamp}`)
        ) as VehicleMapDataPoint;
        const lastDataPoint =
          newSanitizedVehicleMapData[selectedTripIndex][
            newSanitizedVehicleMapData[selectedTripIndex].length - 1
          ];
        setSelectedTripPoint(lastDataPoint);
        dispatch(
          setModalsData({
            ...modalsState,
            showLayerFilter: false,
            showEventFilter: false,
            showVehicleFilter: false,
            showVehicleDetails: true,
          })
        );
        // if crashTrackEventData is present, select the trip that has the timestamp
      } else if (!!crashTrackEventData) {
        const selectedTripIndex = newSanitizedVehicleMapData.findIndex(
          (tripPoints) =>
            tripPoints.some(
              (tripPoint) =>
                tripPoint.timestamp ===
                parseInt(`${crashTrackEventData.assetCrashIdTimestamp}`)
            )
        );
        setSelectedTrip(selectedTripIndex);
        const crashDataPoint = newSanitizedVehicleMapData[
          selectedTripIndex
        ].find(
          (item) =>
            item.timestamp ===
            parseInt(`${crashTrackEventData.assetCrashIdTimestamp}`)
        );
        // highlight and select the crash event
        if (
          !!crashDataPoint &&
          !!APP_CONFIG.CRASHTRACK.HIGHLIGHT_EVENT_ON_INCIDENT_MAP
        ) {
          setTimeout(() => {
            setSelectedTripPoint(crashDataPoint);
            dispatch(
              setModalsData({
                ...modalsState,
                showLayerFilter: false,
                showEventFilter: false,
                showVehicleFilter: false,
                showVehicleDetails: true,
              })
            );
          }, 1000);
        }
      } else {
        // if no buzzerAlertTimestamp and no crashTrackEventData, reset selected trip point
        // disabled resetting selected trip point to preserve user selection
        // console.log("==++== C", null);
        // setSelectedTripPoint(null);
      }
      // filter status codes using newSanitizedVehicleMapData
      const statusCodes = newSanitizedVehicleMapData
        .reduce((acc, item) => {
          const statusCodes: VehicleStatusCode[] = item.map(
            (tripPoint) => tripPoint.statuscode
          );
          return [...acc, ...statusCodes];
        }, [] as VehicleStatusCode[])
        // filtering for unique status codes
        .filter((item, index, self) => self.indexOf(item) === index);
      // 02/10/2025 - disabled filtering for motion status codes per client request
      // filtering out movement status codes
      // .filter(
      //   (item) =>
      //     ![
      //       VehicleStatusCode.STATUS_MOTION_HEADING,
      //       VehicleStatusCode.STATUS_MOTION_MOVING,
      //       VehicleStatusCode.STATUS_MOTION_ENROUTE,
      //       VehicleStatusCode.STATUS_MOTION_EN_ROUTE,
      //     ].includes(item)
      // );
      setFilterableStatusCodes(statusCodes);
      if (!!buzzerAlertTimestamp) {
        const selectedTripIndex = newSanitizedVehicleMapData.findIndex(
          (tripPoints) =>
            tripPoints.some(
              (tripPoint) =>
                tripPoint.timestamp === parseInt(`${buzzerAlertTimestamp}`)
            )
        );
        const buzzerAlertTripPoint = newSanitizedVehicleMapData[
          selectedTripIndex
        ].find(
          (tripPoint) =>
            tripPoint.timestamp === parseInt(`${buzzerAlertTimestamp}`)
        );
        console.error("selectedTripIndex", selectedTripIndex);
        console.error("buzzerAlertTripPoint", buzzerAlertTripPoint);
        setFilteredStatusCodes(
          !!buzzerAlertTripPoint?.statuscode
            ? [buzzerAlertTripPoint?.statuscode]
            : statusCodes
        );
      } else {
        // with `disablemappins` set, start with only ignition on/off shown.
        // The constant rather than an intersection with what's on screen: an
        // empty list means "no filter, pin everything", so a trip with no
        // ignition events would seed the exact opposite of the setting.
        setFilteredStatusCodes(
          mapPinsDisabled ? IGNITION_STATUS_CODES : statusCodes
        );
      }
      loadResetMapDataWithInitialValues();
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("newSanitizedVehicleMapData", newSanitizedVehicleMapData);
    } else {
      setSanitizedVehicleMapData([]);
      // setSelectedTripPoint(null);
    }
  }, [dataVehicleMapData, isFetchingVehicleMapData, isMultiDay]);

  // common map ref to be used for various map operations
  const mapRef = React.useRef<TMapRef>({
    map: null,
    objects: {
      mTripLayers: [],
      mDirectionsManagers: [],
      mTripsVisibility: [],
      mWaypoints: [],
      mPushpins: [],
      mClusterLayer: null,
      mTripsData: [],
    },
    routeViewsEnabled: {
      DEFAULT: true,
      POINT_TO_POINT: false,
      SNAP_TO_ROAD: false,
      DIRECTIONS_SERVICE: false,
    },
  });
  const zoomRouteListenerRef = useRef<any>(null);

  
  const { dateGroups, isFetching: isFetchingMultiDay } = useMultiDateTripData(
    {
      accountId: `${thisUserOrganizationId}`,
      deviceId: dataSingleVehicle?.deviceid,
      selectedDates,
      timezone: accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
    }
  );

  const [selectedTripKeys, setSelectedTripKeys] = useState<Set<string>>(
    new Set()
  );
  const knownTripKeysRef = useRef<Set<string>>(new Set());

  // non-trips are excluded by default; this toggle opts them back in
  const [includeNonTrips, setIncludeNonTrips] = useState(false);
  // on by default - days retracing the same route is the point of this
  const [overlapsEnabled, setOverlapsEnabled] = useState(true);

  // the asset belongs in this as much as the dates do - see the effect below
  const tripSelectionSignatureRef = useRef<string | null>(null);

  // auto-select trips as they load, without overriding manual unchecks
  // within the same asset and date set. non-trips are never auto-selected.
  useEffect(() => {
    // A trip key is dateKey__index with no asset in it, so switching asset
    // on the same dates produces the very same keys. Keyed on the dates
    // alone, knownTripKeysRef kept the previous asset's keys, every trip of
    // the new one read as "already seen", nothing was selected, and the map
    // went on drawing the old selection.
    const signature = `${vehicleId}|${[...selectedDates].sort().join(",")}`;
    const selectionContextChanged =
      tripSelectionSignatureRef.current !== signature;
    if (selectionContextChanged) {
      tripSelectionSignatureRef.current = signature;
      knownTripKeysRef.current = new Set();
    }

    const keysToSelect: string[] = [];
    dateGroups.forEach((group) => {
      group.trips.forEach((points, tripIndex) => {
        const key = tripKeyFor(group.dateKey, tripIndex);
        if (knownTripKeysRef.current.has(key)) return;
        knownTripKeysRef.current.add(key);
        if (!isNonTrip(points)) keysToSelect.push(key);
      });
    });

    if (!selectionContextChanged && keysToSelect.length === 0) return;
    setSelectedTripKeys((prev) => {
      const next = selectionContextChanged ? new Set<string>() : new Set(prev);
      keysToSelect.forEach((key) => next.add(key));
      return next;
    });
  }, [dateGroups, selectedDates, vehicleId]);

  
  const autoAddedNonTripKeysRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!includeNonTrips) {
      // so re-ticking offers them again, even ones unchecked by hand
      autoAddedNonTripKeysRef.current.clear();
      return;
    }
    const keysToAdd: string[] = [];
    dateGroups.forEach((group) => {
      group.trips.forEach((points, tripIndex) => {
        if (!isNonTrip(points)) return;
        const key = tripKeyFor(group.dateKey, tripIndex);
        if (autoAddedNonTripKeysRef.current.has(key)) return;
        keysToAdd.push(key);
      });
    });
    if (keysToAdd.length === 0) return;
    keysToAdd.forEach((key) => autoAddedNonTripKeysRef.current.add(key));
    setSelectedTripKeys((prev) => {
      const next = new Set(prev);
      keysToAdd.forEach((key) => next.add(key));
      return next;
    });
  }, [includeNonTrips, dateGroups]);

  useEffect(() => {
    if (includeNonTrips) return;
    setSelectedTripKeys((prev) => {
      const next = new Set<string>();
      let changed = false;
      prev.forEach((key) => {
        const [dateKey, tripIndexKey] = key.split("__");
        const group = dateGroups.find((g) => g.dateKey === dateKey);
        const points = group?.trips[Number(tripIndexKey)];
        if (points && isNonTrip(points)) {
          changed = true;
          return;
        }
        next.add(key);
      });
      return changed ? next : prev;
    });
  }, [includeNonTrips, dateGroups]);

 
  const [multiDayDetailedTripKey, setMultiDayDetailedTripKey] = useState<
    string | null
  >(null);
  const multiDayDetailedTripKeyRef = useRef<string | null>(null);
  multiDayDetailedTripKeyRef.current = multiDayDetailedTripKey;
  const seededMultiDayFilterTripRef = useRef<string | null>(null);

  
  useEffect(() => {
    if (!isMultiDay) return;
    // keep the same [] reference when there's nothing to compute - other
    // effects key off filteredStatusCodes and re-run on every new array
    if (!multiDayDetailedTripKey) {
      seededMultiDayFilterTripRef.current = null;
      setFilterableStatusCodes((prev) => (prev.length === 0 ? prev : []));
      setFilteredStatusCodes((prev) => (prev === null ? prev : null));
      return;
    }
    if (seededMultiDayFilterTripRef.current === multiDayDetailedTripKey) return;
    seededMultiDayFilterTripRef.current = multiDayDetailedTripKey;
    const [dateKey, tripIndexKey] = multiDayDetailedTripKey.split("__");
    const group = dateGroups.find((g) => g.dateKey === dateKey);
    const points = group?.trips[Number(tripIndexKey)] ?? [];
    const tripStatusCodes = points
      .map((point) => point.statuscode)
      .filter((item, index, self) => self.indexOf(item) === index);
    setFilterableStatusCodes(tripStatusCodes);
    setFilteredStatusCodes(
      mapPinsDisabled ? IGNITION_STATUS_CODES : tripStatusCodes
    );
  }, [isMultiDay, multiDayDetailedTripKey, dateGroups, mapPinsDisabled]);

  // clicking a route or event pin on the map opens that trip's detail and
  // the same event popup the single-day pushpins use
  // the account's configured speed unit - the replay's speed readout and
  // every speed the map itself shows, so they all agree with the rest of
  // the app
  const multiDayOrgSpeedUnit = useLoggedInUserData(
    "speedUnits"
  ) as SPEED_UNIT_VALUES;
  const multiDaySpeedUnit =
    SPEED_UNITS_OPTIONS.find(
      (option) => option.value === multiDayOrgSpeedUnit
    )?.unit ?? SPEED_UNITS_OPTIONS[0].unit;
  const formatMultiDaySpeed = (speedKph: number | null | undefined) =>
    speedKph == null
      ? null
      : `${Math.round(
          convertKmphTo(multiDaySpeedUnit, speedKph)
        )} ${speedUnitSuffix(multiDaySpeedUnit)}`;

  const handleMultiDayTripPointClick = (
    tripKey: string,
    point: VehicleMapDataPoint,
    source?: TripPointClickSource
  ) => {
    // An event pin only exists on a trip that is already open, so clicking
    // one is never a request to change what is on the map - re-opening the
    // trip here would take every pin down and redraw the route out from
    // under the event that was just clicked.
    if (source !== "pin") {
      const routeIsInFocus = multiDayDetailedTripKeyRef.current === tripKey;
      setMultiDayDetailedTripKey(tripKey);
      setIsComparing(false);
      if (!routeIsInFocus) return;
    }
    setSelectedTripPoint(null);
    setTimeout(() => setSelectedTripPoint(point), 100);
    dispatch(
      setModalsData({
        ...modalsState,
        showLayerFilter: false,
        showEventFilter: false,
        showVehicleFilter: false,
        showVehicleDetails: true,
      })
    );
  };

  // "Compare Routes": up to 4 trips side by side. isComparing swaps out the
  // map area; the modal is shared by the initial pick and "Manage Trips".
  const MAX_COMPARE_TRIPS = 4;
  const [compareTripKeys, setCompareTripKeys] = useState<string[]>([]);
  const [isComparing, setIsComparing] = useState(false);
  const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

  // the comparison grid covers the map, so anything that plays out on the
  // map has to leave it first
  const startMultiDayReplayEvents = (tripKey: string) => {
    setIsComparing(false);
    multiDayReplay.startReplay(tripKey);
  };
  const startMultiDayReplayAll = () => {
    setIsComparing(false);
    multiDayMapRender.replay();
  };
  const openMultiDayTripDetail = (tripKey: string | null) => {
    if (tripKey) setIsComparing(false);
    setMultiDayDetailedTripKey(tripKey);
  };

  // everything else that holds trip keys has to let go of them too, or a
  // changed range leaves an open trip detail - or a compare card - pointing
  // at a date that is no longer loaded. The selection itself is reset where
  // it is built, above, so that it can't be undone by ordering: effects run
  // in declaration order, and clearing it from down here would wipe what the
  // auto-select had just put in.
  //
  // vehicleId counts as much as the dates do. A trip key is dateKey__index
  // with no asset in it, so one left over from the previous asset still
  // looks valid - the detail view stayed open on a trip belonging to an
  // asset that is no longer on screen, and the camera never re-framed
  // because fitBoundsToTrip found no entry for that key and did nothing.
  useEffect(() => {
    setMultiDayDetailedTripKey(null);
    setCompareTripKeys([]);
    setIsComparing(false);
    autoAddedNonTripKeysRef.current.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates, vehicleId]);

  const multiDayReplay = useMultiDayReplay({
    mapRef,
    dateGroups,
    speedUnit: multiDaySpeedUnit,
    filteredStatusCodes,
  });

  // bidirectional hover between map and left panel. `source` keeps the panel
  // from auto-scrolling to a row the user is already hovering.
  const [multiDayHoveredTrip, setMultiDayHoveredTrip] = useState<{
    tripKey: string;
    source: "map" | "list";
  } | null>(null);

  // useMultiDayMapRender is called further down, after routeViewsVersion

  // due to page components being dependent on map,
  // after 5 seconds of component mount, check if map script is loaded, load if not
  const { initMap } = useMapInit();
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!mapState?.mapScriptLoaded) {
        initMap();
      }
    }, 1000 * 5);
    return () => clearTimeout(timer);
  }, [mapState?.mapScriptLoaded]);

  // fetch geozones data
  const {
    data: dataOrgGeozones,
    isFetching: isFetchingOrgGeozones,
    isLoading: isLoadingOrgGeozones,
  } = useOrganizationGeozonesQuery(
    {
      accountid: `${thisUserOrganizationId}`,
      page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
      page: 1,
    } as LegacyGeozoneEntityListingPayload,
    { skip: !mapState?.mapScriptLoaded } // || mapState?.mapLayerOptions?.hide_geozones
  );

  // common map ref for geozone map operations
  const mapRefGeozones = React.useRef<TMapRefGeozones>({});

  const renderGeozones = () => {
    if (mapState?.mapLayerOptions?.hide_geozones) return;
    if (
      !!dataOrgGeozones &&
      !isFetchingOrgGeozones &&
      !!mapState?.mapScriptLoaded
    ) {
      dataOrgGeozones?.results
        ?.filter(filterOutWhiteGeozones)
        ?.forEach((geozone) => {
          // initialize map ref for each geozone
          const thisGeozoneMapRef = (mapRefGeozones.current[geozone.geozoneid] =
            {
              current: {
                map: mapRef.current.map,
                objects: {},
              },
            });
          // prepare map state for each geozone
          const geozoneLocs = getGeozoneLocs(
            geozone as Partial<TLegacyGeozone>
          );
          const geozoneCenter = getGeozoneShapeCenter(
            geozone as Partial<TLegacyGeozone>,
            "object"
          ) as TLatLng;
          const mapDataFromSavedRecord = {
            //values.properties as TGeozoneMapData;
            centerPosition: geozoneCenter,
            locs: geozoneLocs,
            radius: geozone.radius,
          };
          const newMapState: TMapState = geozonePrepareMapState({
            seedMapState: mapState,
            shapeType: geozone.zonetype,
            seedMapData: mapDataFromSavedRecord,
            centerPosition:
              mapDataFromSavedRecord?.centerPosition ??
              APP_CONFIG.MAPS.DEFAULTS.CENTER,
            polygonSides:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs
                ?.length ?? APP_CONFIG.MAPS.DEFAULTS.POLYGON_POINTS,
            locs:
              (mapDataFromSavedRecord as TGeozoneMapDataPolygon)?.locs ??
              undefined,
            radius:
              (mapDataFromSavedRecord as TGeozoneMapDataCircle)?.radius ??
              APP_CONFIG.MAPS.DEFAULTS.RADIUS,
            color: hexToRgb(`${geozone.shapecolor}`), // APP_CONFIG.MAPS.COLORS[values.shapecolor as keyof typeof APP_CONFIG.MAPS.COLORS],
            editable: false,
            ready: true,
          });
          const mapOperationsParams = {
            mapRef: thisGeozoneMapRef, // mapRefGeozones.current[geozone.geozoneid],
            mapData: newMapState.mapData || mapDataFromSavedRecord,
            setMapData: () => {},
            drawShapeOnly: true,
            geozoneId:
              loggedInUserData?.user?.enablegeozonelabels === 1
                ? geozone.geozoneid
                : undefined,
          } as TMapOperationsProps;
          if (APP_CONFIG.DEBUG.GEOZONES)
            console.log("geozone", geozone.geozoneid);
          // check zone type and call respective map operations
          switch (parseInt(geozone?.zonetype?.toString())) {
            case LegacyGeozoneType.CIRCLE:
              mapOperationsCircle(mapOperationsParams);
              break;
            case LegacyGeozoneType.POLYGON:
              mapOperationsPolygon(mapOperationsParams);
              break;
            case LegacyGeozoneType.ROUTE:
              mapOperationsRoute(mapOperationsParams);
              break;
            default:
              break;
          }
        });
    }
  };

  // used for any map state transition to refresh map entities
  const [mapStateTransitionInProgress, setMapStateTransitionInProgress] =
    useState(false);

  const loadResetMapDataWithInitialValues = () => {
    if (APP_CONFIG.DEBUG.MAPS)
      console.log("[loadResetMapDataWithInitialValues]");
    setMapStateTransitionInProgress(true);
    const applicableMapCenter =
      userMapRefreshing && !!userMapActivity?.assetMap?.center
        ? userMapActivity?.assetMap?.center
        : APP_CONFIG.MAPS.DEFAULTS.CENTER;
    const newMapState: Partial<TMapState> = {
      // ...mapState,
      mapCenter: applicableMapCenter,
      mapData: {
        centerPosition: applicableMapCenter,
        ready: true,
      },
    };
    if (APP_CONFIG.DEBUG.MAPS) console.log("newMapState", newMapState);
    setTimeout(() => {
      dispatch(setMapStateData(newMapState));
      setMapStateTransitionInProgress(false);
    }, 200);
  };

  const [
    vMCameraEventGetVideoDetailResponse,
    setVMCameraEventGetVideoDetailResponse,
  ] = useState<CameraEventGetVideoDetailResponseVideos | null>(null);

  // visionmax state variables
  const storageLookupTimestamp = useRef<number | null>(null);

  // /**
  //  * To be used as soon as VisionMax support device wakeup using serial number
  //  * Method to trigger request video process by first checking if device is online or offline.
  //  * If device is offline,  then wakeup device
  //  * If device is online, then proceed with request
  //  */
  // const triggerVideoRequestProcess = (timestamp: number) => {
  //   generalInfoApiTrigger(dataSingleVehicle?.deviceid ?? "")
  //     .then((res) => {
  //       if (res && res?.data && res?.data?.data?.length > 0) {
  //         const dataDeviceInfo = res?.data;
  //         const infoData = dataDeviceInfo?.data[0];
  //         if (infoData.connectivity_state === CONNECTIVITY_STATE.online) {
  //           initiateVideoRequest(timestamp);
  //         } else if (
  //           infoData.connectivity_state === CONNECTIVITY_STATE.offline ||
  //           infoData.connectivity_state === CONNECTIVITY_STATE.standby
  //         ) {
  //           // Device is offline, request wakeup
  //           dispatch(
  //             setModalsData({
  //               ...modalsState,
  //               showModalWakeupCamera: {
  //                 cid: dataSingleVehicle?.cameraid, // infoData.cid,
  //                 serial_number: dataSingleVehicle?.uniqueid, // infoData.serial_number,
  //                 onSuccessCallback: () => initiateVideoRequest(timestamp),
  //                 onFailureCallback: () => {},
  //                 onCancelCallback: () => {},
  //               } as CameraWakeupRequiredInfo,
  //             })
  //           );
  //         } else {
  //           // Device state is unknown, cancel any play or download request
  //           resetTask();
  //         }
  //       }
  //     })
  //     .catch((error) => {
  //       // Unable to get device info, cancel any play or download request
  //       resetTask();
  //     });
  // };

  const handleRoutePointClick = (timestamp: number) => {
    // Only retrieve video if recorder is of type VisionMax and recording is supported
    if (
      thisOrgCameraRecorderType !== ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ||
      !dataSingleVehicle?.is_recording_supported ||
      !timestamp
    ) {
      return;
    }
    dispatch(
      setModalsData({
        ...modalsState,
        showRequestVideo: {
          show: true,
          timestamp: timestamp,
        },
      })
    );
  };

  // const triggerVideoPlayback = () => {
  //   if (!storageLookupTimestamp.current) return;
  //   dispatch(
  //     setModalsData({
  //       ...modalsState,
  //       showRequestVideo: {
  //         show: false,
  //         timestamp: storageLookupTimestamp.current,
  //       },
  //       showModalVideoPlayback: {
  //         startTimestamp: storageLookupTimestamp.current,
  //         duration: 30, // default duration in seconds
  //         captureMode: RECORDING_TYPE.sideBySide, // CAMERA_CAPTURE_MODES.front
  //         action: "play",
  //       },
  //     })
  //   );
  // };

  const requestVisionmaxVideo = () => {
    toast.info(
      "Requesting video from the device, this may take a couple of minutes. Please do not close the modal or navigate to another page."
    );
    requestVideoVM({
      timestamp: storageLookupTimestamp.current as number, // deviceEventFromState?.timestamp,
      serial_number: dataDeviceListing?.data?.[0]?.serial_number ?? "", // cameraId,
      duration_in_seconds:
        APP_CONFIG.VISIONMAX.REQUEST_VIDEO.DEFAULT_CLIP_DURATION_SEC, // request default 30 seconds of video
      cam_id: CAMERA_CAPTURE_MODES.combined,
    })
      .unwrap()
      .then((res) => {
        toast.success(
          "File has been requested successfully. Your file will be available in File Retrievals once it is ready."
        );
      })
      .catch((error) => {
        toast.error("Error requesting video");
        console.log("error requesting video", error);
      });
  };

  const initiateVideoRequest = (timestamp: number) => {
    storageLookupTimestamp.current = timestamp;
    generalInfoApiTrigger(dataDeviceListing?.data?.[0]?.cid ?? "")
      .then((res) => {
        if (res && res?.data && res?.data?.data?.length > 0) {
          const dataDeviceInfo = res?.data;
          const infoData = dataDeviceInfo?.data[0];
          console.log("infoData", infoData);
          // check if device is online or offline
          if (infoData.connectivity_state === CONNECTIVITY_STATE.online) {
            requestVisionmaxVideo();
          } else if (
            infoData.connectivity_state === CONNECTIVITY_STATE.offline ||
            infoData.connectivity_state === CONNECTIVITY_STATE.standby
          ) {
            console.log("wakeup required for device", infoData);
            // Device is offline, request wakeup
            setTimeout(() => {
              dispatch(
                setModalsData({
                  ...modalsState,
                  showRequestVideo: {
                    show: false,
                    timestamp: timestamp, // deviceEventFromState?.timestamp,
                  },
                  showModalWakeupCamera: {
                    cid: dataDeviceListing?.data?.[0]?.cid, // infoData.cid,
                    serial_number: dataDeviceListing?.data?.[0]?.serial_number, // infoData.serial_number,
                    onSuccessCallback: () => requestVisionmaxVideo(),
                    onFailureCallback: () => {
                      storageLookupTimestamp.current = null;
                    },
                    onCancelCallback: () => {
                      storageLookupTimestamp.current = null;
                    },
                  } as CameraWakeupRequiredInfo,
                })
              );
            }, 300);
          }
        }
      })
      .catch((error) => {});
    // setRetrievingVideo(true);
    // toast.info(
    //   "Requesting video from the device, this may take a couple of minutes. Please do not close the modal or navigate to another page."
    // );
    // requestVideoVM({
    //   timestamp: timestamp as number, // deviceEventFromState?.timestamp,
    //   serial_number: dataSingleVehicle?.cameraid ?? "", // cameraId,
    //   duration_in_seconds: APP_CONFIG.VISIONMAX.REQUEST_VIDEO.DEFAULT_CLIP_DURATION_SEC,
    //   // cam_id:
    //   //   recordingType === RECORDING_TYPE.sideBySide
    //   //     ? CAMERA_CAPTURE_MODES.combined
    //   //     : CAMERA_CAPTURE_MODES.front,
    //   // device_id: assetId,
    //   // event_id: deviceEventFromState?.status.statuscode,
    // })
    //   .unwrap()
    //   .then((res) => {
    //     // Get the task id from response and retrieve status of task
    //     const taskId = res?.data?.task_id;
    //     if (taskId) {
    //       setTaskId(taskId);
    //       setStartTaskUpdates(true); // Enable API to fetch task updates
    //     } else {
    //       resetTask();
    //       toast.error("Error requesting video");
    //     }
    //   })
    //   .catch((error) => {
    //     toast.error("Error requesting video");
    //     console.log("error requesting video", error);
    //     resetTask();
    //   });
  };

  // set map data on page load
  // useEffect(() => {
  //   if(!!dataSingleVehicle && !isFetchingSingleVehicle && !!dataVehicleMapData && !isFetchingVehicleMapData) {
  //     setDataPoints(sanitizedVehicleMapData);
  //     loadResetMapDataWithInitialValues();
  //   }
  // }, [dataSingleVehicle, isFetchingSingleVehicle, dataVehicleMapData, isFetchingVehicleMapData]);

  // const [dataPoints, setDataPoints] = useState<VehicleMapDataPoint[][]>([]);

  // helper function to get map operations props
  const getMapOpsProps: TMemoizedMapOperationsProps = useCallback(() => {
    let _sanitizedVehicleMapData: VehicleMapDataPoint[][] =
      sanitizedVehicleMapData.map((trip) =>
        trip.map((point) => ({ ...point }))
      );
    if (selectedSorting === "desc") {
      _sanitizedVehicleMapData.reverse();
    }

    // feature: "for consecutive events with no speed, take the first lat long
    // and display the list in the left side so that user can select"
    // -----------------------------------------------------------------------
    let lastTripPoint: VehicleMapDataPoint | null = null;
    // _sanitizedVehicleMapData.map((assetTrip, i) => {
    //   lastTripPoint = null;
    //   assetTrip.map((tripPoint, j) => {
    //     let groupableEventCodes = getGroupableEventCodes(tripPoint);
    //     if (lastTripPoint === null) {
    //       lastTripPoint = tripPoint;
    //     } else {
    //       if (groupableEventCodes?.includes(tripPoint.statuscode)) {
    //         // tripPoint statuscode exists in event codes group
    //         // thus, we will overwrite the lat/lng
    //         _sanitizedVehicleMapData[i][j].latitude = lastTripPoint.latitude;
    //         _sanitizedVehicleMapData[i][j].longitude = lastTripPoint.longitude;
    //       } else {
    //         // tripPoint statuscode does not exist in event codes group
    //         // thus, we will reset the lastTripPoint
    //         lastTripPoint = null;
    //       }
    //     }
    //   });
    // });
    // 9/3/2025 approach change suggested by client: any repeating status code
    // with no speed will have geo-coordinates overwritten to the first instance
    // this makes the feature independent of specific status codes
    // as a result, these event icons will show stacked one over the other on the map
    _sanitizedVehicleMapData.forEach((assetTrip, i) => {
      lastTripPoint = null;
      assetTrip.forEach((tripPoint, j) => {
        // let groupableEventCodes = getGroupableEventCodes(tripPoint);
        if (lastTripPoint === null) {
          lastTripPoint = tripPoint;
        } else {
          const { latitude, longitude } = lastTripPoint;
          if (
            tripPoint.speedkph === 0
            // 10/3/2025 after client's email regarding this, the last event type check has been removed (below)
            // and the code stacks any and every consecutive event icon with no speed stacked one over the other
            // -------------------------------------------------------------------------------------------------
            // &&
            // tripPoint.statuscode ===
            //   lastTripPoint.statuscode /*groupableEventCodes?.includes(tripPoint.statuscode)*/
          ) {
            // tripPoint has no speed and event has repeated
            // thus, we will overwrite the lat/lng
            // and these event icons will show stacked one over the other on the map
            _sanitizedVehicleMapData[i][j] = {
              ..._sanitizedVehicleMapData[i][j],
              latitude,
              longitude,
            };
          } else {
            // tripPoint is new event
            // thus, we will reset the lastTripPoint
            lastTripPoint = tripPoint;
          }
        }
      });
    });

    return {
      mapRef,
      mapState,
      sparseeventreporting: dataSingleVehicle?.sparseeventreporting,
      mapData: { ...mapState?.mapData } as TMapData,
      mapLayerOptions: mapState?.mapLayerOptions as TMapLayerOptions,
      setMapData: () => {},
      dataPoints: _sanitizedVehicleMapData,
      selectedSorting,
      selectedTripIndex:
        selectedTrip === null
          ? null
          : selectedSorting === "desc"
            ? sanitizedVehicleMapData.length - 1 - selectedTrip
            : selectedTrip,
      selectedStatusCodes: filteredStatusCodes,
      onViewChangeEnd: (center, zoom) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log("[onViewChangeEnd] center:", center, "zoom:", zoom);
        dispatch(
          setMapStateData({
            // ...mapState,
            mapCenter: center,
            mapZoom: zoom,
            mapData: {
              ...mapState.mapData,
              centerPosition: center,
            },
            mapType:
              gMapGetMode(mapRef.current.map) ===
              GOOGLE_MAP_MODE_NAMES.STREETVIEW
                ? (APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
                    .type as TMapType)
                : mapRef.current.map?.getMapTypeId() ===
                    APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE]
                      .id
                  ? (APP_CONFIG.MAPS.TYPES[
                      APP_CONFIG.MAPS.TYPES_INDEX.SATELLITE
                    ].type as TMapType)
                  : mapState.mapType,
          })
        );
        // this function gets triggered on closing streetside view
        // make map entities visible here
        if (
          mapState?.mapType ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].type &&
          mapRef.current.map?.getMapTypeId() ===
            APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP].id
        ) {
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(
              `Attempt to switch to road view (when closing streetside), params: ${mapState?.mapType}`
            );
        }
        // if(APP_CONFIG.DEBUG.MAPS) console.log('Switching to road view after streetside view is closed')
        // handleMapTypeChange('road')
      },
      onMapTypeChange: (event) => {
        if (APP_CONFIG.DEBUG.MAPS) console.log("[onMapTypeChange]", event);
        // dispatch(
        //   setMapStateData({
        //     ...mapState,
        //     mapType:
        //       (APP_CONFIG.MAPS.TYPES.find(
        //         (item) => item.id === event.newMapTypeId
        //       )?.type as TMapType) ?? "",
        //   })
        // );
      },
      // if this is a refresh, send override param
      ...(!!userMapActivity?.assetMap?.center &&
      !!userMapActivity?.assetMap?.zoom &&
      !!userMapRefreshing
        ? {
            overrideMapView: {
              center: userMapActivity?.assetMap?.center || null,
              zoom:
                userMapActivity?.assetMap?.zoom ||
                APP_CONFIG.MAPS.DEFAULTS.ZOOM,
              mapType: userMapActivity?.assetMap?.mapType || null,
            },
          }
        : {}),
      onRoutePointClick: handleRoutePointClick,
      ...(!!crashTrackEventData && !!crashTrackEventData?.assetCrashIdTimestamp
        ? {
            crashTrackImpactTimestamp: parseInt(
              crashTrackEventData?.assetCrashIdTimestamp
            ),
          }
        : {}),
    };
  }, [
    mapState,
    sanitizedVehicleMapData,
    selectedTrip,
    filteredStatusCodes,
    crashTrackEventData,
    userMapActivity,
    userMapRefreshing,
  ]);

  // the multi-day renderer draws only once the google map exists. Waiting on
  // the trip data alone raced it: when the data came back first - a reload,
  // with the API response cached - the draw found no map, gave up, and
  // nothing asked it to try again, leaving a blank map and a "Drawing route"
  // panel that never finished.
  const [isMapReady, setIsMapReady] = useState(false);

  // Bumped every time BasicMap hands us a map, which is once per
  // google.maps.Map instance. isMapReady alone cannot carry this: it only
  // ever goes false -> true, so when the map is torn down and rebuilt,
  // setIsMapReady(true) is a no-op, nothing re-renders, and the renderer
  // keeps drawing against the destroyed instance through a mapRef nobody
  // clears. This is the signal the renderer needs to start over.
  const [mapInstanceVersion, setMapInstanceVersion] = useState(0);

  // carry out map operations on map ready
  const handleMapReady = useCallback(() => {
    setIsMapReady(true);
    setMapInstanceVersion((version) => version + 1);
    const map = mapRef.current.map;
    const currentMapType = mapState?.mapType;
    // call centralized function to set the initial view (based on user activity for map refresh or defaults)
    if (map && currentMapType) {
      gMapSetMode(
        map,
        userMapActivity?.assetMap?.mapType || (currentMapType as any),
        !!userMapActivity?.assetMap?.center
          ? new google.maps.LatLng(
              userMapActivity.assetMap.center.latitude,
              userMapActivity.assetMap.center.longitude
            )
          : map.getCenter(),
        {
          onStreetViewVisibleChanged,
        }
      );
    }

    mapOperations(
      {
        ...getMapOpsProps(),
        ...(errorVehicleMapData ? { dataPoints: [] } : {}),
        onDataPointPushpinClick: (dataPoint: VehicleMapDataPoint) => {
          if (clearSearchRef.current) {
            clearSearchRef.current(true);
          }
          if (APP_CONFIG.DEBUG.MAPS)
            console.log("onDataPointPushpinClick", dataPoint);
          setSelectedTripPoint(null);
          setTimeout(() => {
            setSelectedTripPoint(dataPoint);
          }, 100);
          // open vehicle details modal
          dispatch(
            setModalsData({
              ...modalsState,
              showLayerFilter: false,
              showEventFilter: false,
              showVehicleFilter: false,
              showVehicleDetails: true,
            })
          );
        },
        // if this is a refresh, send override param
        ...(!!userMapActivity?.assetMap?.center &&
        !!userMapActivity?.assetMap?.zoom &&
        !!userMapRefreshing
          ? {
              overrideMapView: {
                center: userMapActivity?.assetMap?.center || null,
                zoom:
                  userMapActivity?.assetMap?.zoom ||
                  APP_CONFIG.MAPS.DEFAULTS.ZOOM,
                mapType: userMapActivity?.assetMap?.mapType || null,
              },
            }
          : {}),
      },
      sanitizedVehicleMapData
    );
    renderGeozones();

    // if a trip / trip point was selected prior to map refresh, re-select it
    // only re-select when the stored selection is different from current selection
    const storedSelectedTrip = userMapSelection?.assetMap?.selectedTrip;
    if (
      typeof storedSelectedTrip === "number" &&
      storedSelectedTrip !== selectedTrip // important condition, removing this triggers an infinite loop
    ) {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("re-select trip after map is ready", storedSelectedTrip);
      // avoid unnecessary re-dispatches that can cause remount/refetch loops
      setTimeout(() => {
        handleTripSelection(storedSelectedTrip as number);
      }, 300);
    }

    // if a trip point was selected prior to map refresh, re-select it
    if (!!userMapSelection?.assetMap?.selectedTripPoint) {
      console.log(
        "re-select trip point after map is ready",
        userMapSelection?.assetMap?.selectedTripPoint
      );
      if (
        !!userMapActivity?.assetMap?.center &&
        !!userMapActivity?.assetMap?.zoom &&
        !!userMapRefreshing
      ) {
        // do nothing
      } else {
        setTimeout(() => {
          mapUpdatesHandler(
            getMapOpsProps(),
            "tripPointSelectionChange",
            userMapSelection?.assetMap?.selectedTripPoint
          );
        }, 600);
      }
    }
  }, [
    mapState,
    gMapSetMode,
    sanitizedVehicleMapData,
    selectedTrip,
    filteredStatusCodes,
    errorVehicleMapData,
    mapState?.mapLayerOptions?.hide_geozones,
    userMapActivity,
    userMapSelection,
    userMapRefreshing,
  ]); // instead of [mapState?.mapData]

  // trigger map state transition on selected trip change or filtered status codes change
  useEffect(() => {
    // single-day only. multi-day also sets filteredStatusCodes, which would
    // otherwise remount BasicMap on every detail change.
    if (isMultiDay) return;
    // hide all modals
    dispatch(
      setModalsData({
        ...modalsState,
        showLayerFilter: false,
        showEventFilter: false,
        ...(userMapRefreshing
          ? {}
          : {
              showVehicleDetails: !!buzzerAlertTimestamp
                ? modalsState?.showVehicleDetails
                : false,
            }),
        showVehicleFilter: false,
      })
    );
    if (!buzzerAlertTimestamp) {
      // reset selected trip point if buzzerAlertTimestamp is not present
      // disabled resetting selected trip point to preserve user selection
      if (!userMapRefreshing) {
        setSelectedTripPoint(null);
      }
    }
    // this toggle remounts BasicMap, rebuilding the Google Map instance.
    // skip it during initial load so the map isn't rebuilt twice before
    // the user has done anything.
    if (mapTransitionReadyRef.current) {
      setMapStateTransitionInProgress(true);
      setTimeout(() => {
        setMapStateTransitionInProgress(false);
      }, 100);
    } else if (filteredStatusCodes !== null) {
      // filters populated - treat further changes as user-driven
      mapTransitionReadyRef.current = true;
    }
    // update store with selected trip
    dispatch(
      setUserMapSelection({
        ...userMapSelection,
        assetMap: {
          ...userMapSelection.assetMap,
          selectedTrip: selectedTrip,
        },
      })
    );
    // if selectedTrip updates to null and map type is streetside, switch to roadmap
    // this addresses the scenario where user clicks on the back button after being in streetside mode on the selected trip details view (with a trip point selected)
    if (
      selectedTrip === null &&
      mapState?.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW
    ) {
      handleMapTypeChange(
        APP_CONFIG.MAPS.TYPES[APP_CONFIG.MAPS.TYPES_INDEX.ROADMAP]
          .type as TMapType
      );
    }
  }, [selectedTrip, filteredStatusCodes /*, userMapRefreshing*/]);

  // trigger map state transition on hovered trip change
  useEffect(() => {
    // single-day only. hoveredTrip is always null in multi-day, and
    // setTripHoverState reads null as "show every trip", which puts the
    // legacy routes back on the map.
    if (isMultiDay) return;
    mapUpdatesHandler(
      getMapOpsProps(),
      "setTripHoverState",
      // value
      selectedSorting === "desc"
        ? hoveredTrip === null
          ? null
          : sanitizedVehicleMapData.length - 1 - hoveredTrip
        : hoveredTrip,
      // additional data
      { routeViewsEnabled: mapRef.current.routeViewsEnabled }
    );
  }, [hoveredTrip, isMultiDay]);

  const [searchedVehiclesQuery, { isFetching: isFetchingSearchedVehicles }] =
    useLazyOrganizationVehiclesQuery();

  const vehicleDropdownOption = (vehicleItem: TLegacyVehicle) => {
    const labelText = consistentDisplayAssetItem(
      vehicleItem.deviceid,
      vehicleItem.description,
      vehicleItem.displayname,
      vehicleItem.drivername,
      showDriver
    );
    // const labelText = `${vehicleItem.deviceid}${vehicleItem.description ? ` | ${vehicleItem.description}` : ""}${vehicleItem.displayname ? ` | ${vehicleItem.displayname}` : ""}`;
    return {
      value: vehicleItem.deviceid, // getVehicleUUID(vehicleItem),
      label: labelText,
    } as TSelectboxOption;
  };

  const loadOptionsHandlerVehicleAutosuggest = async (
    inputValue: string,
    prevOptions: OptionsOrGroups<unknown, GroupBase<unknown>>,
    additional?: { page: number }
  ) => {
    // if (inputValue.length < 3) {
    //   const result: OptionsOrGroups<
    //     unknown,
    //     GroupBase<unknown>
    //   > = dataOrgVehicles?.results?.map((vehicleItem) =>
    //     vehicleDropdownOption(vehicleItem)
    //   ) ?? [];
    //   return {
    //     options: result,
    //     hasMore: false,
    //     additional: {
    //       page: 2,
    //     },
    //   };
    // }
    const { data: searchedVehiclesResponse } = await searchedVehiclesQuery({
      ...orgVehiclesQueryParams?.current,
      search: inputValue,
      // 'search_fields' intentionally not added here as default search fields are defined in the API service
      isactive: 1, // active vehicles only
      page: additional?.page ?? 2,
    });

    if (APP_CONFIG.DEBUG.ALL)
      console.log("searchedVehiclesResponse", searchedVehiclesResponse);
    const options: OptionsOrGroups<
      unknown,
      GroupBase<unknown>
    > = searchedVehiclesResponse?.results?.map((searchedVehicleItem) =>
      vehicleDropdownOption(searchedVehicleItem)
    ) ?? [];
    return {
      options,
      hasMore: !!searchedVehiclesResponse?.next,
      additional: {
        page: additional?.page ? (additional?.page as number) + 1 : 2,
      },
    };
  };
  const debouncedLoadOptionsHandlerVehicleAutosuggest = asyncDebounce(
    loadOptionsHandlerVehicleAutosuggest,
    APP_CONFIG.DEBOUNCE.SEARCH
  );

  const handleSelectedVehicleChange = (
    selectedOption: TSelectboxOption | null
  ) => {
    if (APP_CONFIG.DEBUG.ALL)
      console.log("handleSelectedVehicleChange", selectedOption);
    navigate(
      `${routeUrls.dashboardChildren.mapsChildren.vehicle}/${getVehicleUUID({
        accountid: `${thisUserOrganizationId}`,
        deviceid: selectedOption?.value,
        vehicleid:
          dataOrgVehicles?.results.find(
            (item) => item.deviceid === selectedOption?.value
          )?.vehicleid || selectedOption?.value,
      })}`
    );
  };

  const [gImageryMetaApiTrigger, { isFetching: isFetchingGImageryMeta }] =
    useLazyGImageryMetaQuery();

  const onStreetViewVisibleChanged = useCallback(
    (panoVisible: boolean) => {
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("[onStreetViewVisibleChanged] attached at map-asset.tsx");
      const newMapMode = gIdentifyAndSetMapModeToStore(mapRef.current.map);
      if (APP_CONFIG.DEBUG.MAPS)
        console.log("SVCHANGED newMapMode:", newMapMode);
      const panorama = mapRef.current.map.getStreetView();
      setTimeout(() => {
        if (!panorama.getVisible()) {
          // exited streetview
          const newMapMode = gIdentifyAndSetMapModeToStore(mapRef.current.map);
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(
              "SVCHANGED - pano not visible (exited) newMapMode:",
              newMapMode
            );
          // update user map activity in store
          // if (!recordActivityAsPage || refreshingRef.current) return;
          // if (userMapRefreshing) {
          //   if (APP_CONFIG.DEBUG.MAPS)
          //     console.log(
          //       "SVCHANGED - userMapRefreshing =",
          //       userMapRefreshing,
          //       "RETURNING"
          //     );
          //   return;
          // }
          if (APP_CONFIG.DEBUG.MAPS)
            console.log(
              "SVCHANGED - about to trigger usermapactivity change",
              newMapMode
            );
          // check if streetview exit was not successful, trigger change to roadmap
          if (
            newMapMode === GOOGLE_MAP_MODE_NAMES.STREETVIEW ||
            mapRef.current.map.getMapTypeId() ===
              GOOGLE_MAP_MODE_NAMES.STREETVIEW
          ) {
            if (APP_CONFIG.DEBUG.MAPS)
              console.log(
                "SVCHANGED - fix incorrect 'newMapMode' value from 'streetview' to ",
                GOOGLE_MAP_MODE_NAMES.ROADMAP
              );
            gMapSetMode(
              mapRef.current.map,
              GOOGLE_MAP_MODE_NAMES.ROADMAP,
              // latLng || mapRef.current.map.getCenter()
              mapRef.current.map.getCenter()
            );
          }
          setTimeout(() => {
            const newMapMode = gIdentifyAndSetMapModeToStore(
              mapRef.current.map
            );
            if (APP_CONFIG.DEBUG.MAPS)
              console.log(
                "SVCHANGED - about to trigger setUserMapActivity change - newMapMode:",
                newMapMode
              );
            dispatch(
              setUserMapActivity({
                ...userMapActivity,
                assetMap: {
                  // ...activityRef.current?.fleetMap,
                  ...userMapActivity?.assetMap,
                  center: {
                    latitude: mapRef.current.map.getCenter().lat(),
                    longitude: mapRef.current.map.getCenter().lng(),
                  } as TLatLng,
                  zoom: mapRef.current.map.getZoom(),
                  mapType: newMapMode,
                },
              } as TUserMapActivity)
            );
          }, 600);
        } else {
          if (APP_CONFIG.DEBUG.MAPS) console.log("SVCHANGED - pano is visible");
        }
      }, 600);
    },
    [userMapActivity, userMapRefreshing]
  );

  const handleMapTypeChange = useCallback(
    (mapType: TMapType, latLng?: google.maps.LatLng) => {
      if (APP_CONFIG.DEBUG.MAPS) console.log("[handleMapTypeChange]", mapType);

      const successCallback = (gImageryMetaApiResponse?: any) => {
        if (APP_CONFIG.DEBUG.MAPS)
          console.log(
            "successCallback",
            latLng,
            mapRef.current.map.getCenter()
          );
        dispatch(
          // update user activity in store
          setUserMapActivity({
            ...userMapActivity,
            assetMap: {
              ...userMapActivity.assetMap,
              mapType: mapType ?? gMapGetMode(mapRef.current.map),
            },
          })
        );
        // set map mode using Google Maps JS SDK
        gMapSetMode(
          mapRef.current.map,
          mapType as TMapType,
          latLng || mapRef.current.map.getCenter(),
          {
            gImageryMetaApiResponse,
            // callback checks if streetside mode is enabled and sets applicable map type in store
            onStreetViewVisibleChanged,
          }
        );
        // update the redux state
        // dispatch(
        //   setMapStateData({
        //     ...mapState,
        //     mapType,
        //   })
        // );
      };

      // Handle street view
      if (
        APP_CONFIG.MAPS.VIEW_TYPES_REQUIRING_METADATA_CHECK.includes(mapType)
      ) {
        const center: google.maps.LatLng = mapRef.current.map.getCenter();
        if (
          !center ||
          typeof center.lat() !== "number" ||
          typeof center.lng() !== "number"
        ) {
          if (APP_CONFIG.DEBUG.MAPS)
            console.error("Map center coords not found");
          toast.error(tMain("maps.type_not_supported"));
          return;
        }

        gMapEnsureAndLaunchStreetView({
          center,
          successCallback,
        });
      } else {
        successCallback();
      }
    },
    [
      dispatch,
      // userMapActivity,
      gMapSetMode,
      // gMapGetMode,
      gIdentifyAndSetMapModeToStore,
    ]
  );

  const handleMapLayerChange = (mapLayerOptions: TMapLayerOptions) => {
    setMapStateTransitionInProgress(true);
    setTimeout(() => {
      dispatch(
        setMapStateData({
          // ...mapState,
          mapLayerOptions,
        })
      );
      setMapStateTransitionInProgress(false);
    }, 100);
  };

  const handleModalCloseAndReset = useCallback(() => {
    // open the vehicle details popup
    // use the currently selected event data
    // if a specific event is already selected and in focus
    if (selectedTripPoint) {
      // event is selected, just close the filter modal.
      // DO NOT reset the map view. keeps the event in focus.
      dispatch(
        setModalsData({
          ...modalsState,
          showLayerFilter: false,
          showVehicleDetails: true,
        })
      );
    } else {
      // Reset map to its default state
      // reset map to default view
      mapUpdatesHandler(
        getMapOpsProps(),
        "centerToPushpin",
        sanitizedVehicleMapData
      );
    }

    // // Keep the 'filteredStatusCodes' state.
    // setMapStateTransitionInProgress(true);
    // setTimeout(() => {
    //   setMapStateTransitionInProgress(false);
    // }, 100);
  }, [
    selectedTripPoint,
    dispatch,
    modalsState,
    sanitizedVehicleMapData,
    getMapOpsProps,
  ]);

  const handleTripSelection = (tripIndex: number | null) => {
    setSelectedTrip(tripIndex);
    // update event status codes
    if (tripIndex === null) {
      setFilteredStatusCodes(
        mapPinsDisabled ? IGNITION_STATUS_CODES : filterableStatusCodes
      );
    } else {
      const tripStatusCodes = (sanitizedVehicleMapData[tripIndex] || [])
        .map((tripPoint) => tripPoint.statuscode)
        // filtering for unique status codes
        .filter((item, index, self) => self.indexOf(item) === index);
      // 02/10/2025 - disabled filtering for motion status codes per client request
      // filtering out movement status codes
      // .filter(
      //   (item) =>
      //     ![
      //       VehicleStatusCode.STATUS_MOTION_HEADING,
      //       VehicleStatusCode.STATUS_MOTION_MOVING,
      //       VehicleStatusCode.STATUS_MOTION_ENROUTE,
      //       VehicleStatusCode.STATUS_MOTION_EN_ROUTE,
      //     ].includes(item)
      // );
      setFilterableStatusCodes(tripStatusCodes);
      setFilteredStatusCodes(
        mapPinsDisabled ? IGNITION_STATUS_CODES : tripStatusCodes
      );
    }
  };

  // ensure refreshPausedRefAlias stays in sync with refreshPaused state
  useEffect(() => {
    refreshPausedRefAlias.current = refreshPaused;
  }, [refreshPaused]);

  // ensure userMapRefreshingRefAlias is always up to date with userMapRefreshing value
  useEffect(() => {
    userMapRefreshingRefAlias.current = userMapRefreshing;
  }, [userMapRefreshing]);

  // activate loader while map refreshes to load the selectedTrip change
  useEffect(() => {
    setHideMapNearRefresh(true);
    setTimeout(() => {
      setHideMapNearRefresh(false);
    }, 2 * 1000);
  }, [selectedTrip]);

  // handle trip point selection change
  useEffect(() => {
    // only if refresh is not in progress
    if (!hideMapNearRefresh) {
      // disabled this `tripPointSelectionChange` call as this is preventing the map user activity
      // location/zoom from being re-selected (displayed) after map refresh
      // (having this enabled was resetting the map to show the selected trip after a map refresh)
      // ------------------------------
      // (4/16/2026) disabling this `tripPointSelectionChange` call was preventing the selected trip point from being zoomed and shown highlighted in the center (via user interaction and map refresh).
      // Enabling this again with userMapRefresh value check (via ref alias) to ensure both:
      // 1) selecting a trip point by user interaction triggers the expected zoom and highlight of the selected trip point in the center, and
      // 2) after map refresh, the recorded user activity (zoom and pan) is applied.
      // ------------------------------
      if (userMapRefreshingRefAlias.current !== true) {
        mapUpdatesHandler(
          getMapOpsProps(),
          "tripPointSelectionChange",
          selectedTripPoint
        );
      }
    }
    // update store with selected trip point
    dispatch(
      setUserMapSelection({
        ...userMapSelection,
        assetMap: {
          ...userMapSelection.assetMap,
          selectedTripPoint: selectedTripPoint,
        },
      })
    );
  }, [selectedTripPoint, hideMapNearRefresh]); // adding hideMapNearRefresh as dependency here ensures that map shows the correct applicable view after map refresh

  const [replayInProgress, setReplayInProgress] = useState(false);
  const [replayPercentage, setReplayPercentage] = useState<number | null>(null);
  const onTripReplayEvents = (tripIndex: number) => {
    if (replayInProgress) return;
    const mapLiveUpdatesAreActive = !refreshPausedRefAlias.current;
    if (mapLiveUpdatesAreActive) {
      toast.info(
        "Live updates are being paused and will be resumed after trip replay is complete",
        { autoClose: 7000 }
      );
      setRefreshPaused(true);
    }
    setReplayInProgress(true);
    mapUpdatesHandler(getMapOpsProps(), "tripReplayEvents", {
      tripIndex,
      replayPercChangeCallback: (perc: number | null) => {
        setReplayPercentage(perc);
      },
      completeCallback: () => {
        setReplayPercentage(null);
        setReplayInProgress(false);
        // resume live updates after replay is complete
        if (mapLiveUpdatesAreActive) {
          toast.info("Resuming live updates");
          setRefreshPaused(false);
        }
      },
    });
  };

  useEffect(() => {
    if (errorVehicleMapData) {
      // toast.error(t('no_trip_data')); // allow the error from backend to show, this toast becomes redundant
      setMapStateTransitionInProgress(true);
      setTimeout(() => {
        setMapStateTransitionInProgress(false);
      }, 100);
    }
  }, [errorVehicleMapData]);

  useEffect(() => {
    const total = parseInt(refreshFrequency, 10) * 1000;
    refreshTotalMsRef.current = total;
    // schedule next refresh from now
    nextRefreshAtRef.current = Date.now() + total;
    refreshProgressRef.current = 100;

    const pollingInterval = setInterval(() => {
      if (selectedTrip !== null && !liveUpdatesHeld) {
        if (APP_CONFIG.DEBUG.ALL) {
          console.log(
            `[Map-Asset] Refetching vehicle map data for selected trip index ${selectedTrip} as per user preference - refresh every ${refreshFrequency} seconds`
          );
        }
        setHideMapNearRefresh(true);
        dispatch(setUserMapRefreshing(true)); // this ensures that map activity is not recorded while refreshing
        refetchVehicleMapData();
        // after triggering refresh, schedule next refresh from now so UI aligns with actual poll
        nextRefreshAtRef.current = Date.now() + total;
        refreshProgressRef.current = 100;
      }
    }, total);

    return () => {
      clearInterval(pollingInterval);
    };
  }, [selectedTrip, refetchVehicleMapData, liveUpdatesHeld, refreshFrequency]);

  // Render progress bar based on nextRefreshAtRef using requestAnimationFrame so the visual
  // countdown is always aligned with the polling schedule (single source of truth).
  useEffect(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (liveUpdatesHeld) return () => {};

    if (!nextRefreshAtRef.current) {
      nextRefreshAtRef.current = Date.now() + refreshTotalMsRef.current;
      refreshProgressRef.current = 100;
    }

    const loop = () => {
      const nextAt = nextRefreshAtRef.current;
      if (!nextAt) return;
      const remaining = Math.max(0, nextAt - Date.now());
      const pct = (remaining / refreshTotalMsRef.current) * 100;
      // update ref only — visual updates are handled by a dedicated component
      refreshProgressRef.current = Math.max(0, Math.round(pct));
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [liveUpdatesHeld, refreshFrequency]);

  // If another part of the app triggers a refresh, align our next-refresh timestamp
  useEffect(() => {
    if (userMapRefreshing) {
      nextRefreshAtRef.current = Date.now() + refreshTotalMsRef.current;
      refreshProgressRef.current = 100;
    }
  }, [userMapRefreshing]);

  // details for buzzer alert display
  // const vehicleHasValidCoords = mapVehicleHasValidCoords(dataPoint);
  const vehicleDataPoint = {
    ...dataSingleVehicle,
    eventData:
      sanitizedVehicleMapData[selectedTrip ?? 0]?.[0] ?? ({} as TDataPoint),
  } as TDataPoint;
  const vehicleOrientation = mapVehicleOrientation(vehicleDataPoint);

  // if map searchbox opens, pause map refresh
  useEffect(() => {
    setRefreshPaused(mapSearchboxOpen ? true : initialRefreshPaused.current);
    // close modals that may obstruct the searchbox functionality
    if (mapSearchboxOpen) {
      setRefreshOptionsExpanded(!mapSearchboxOpen);
      setSelectedTripPoint(null);
      dispatch(
        setModalsData({
          ...modalsState,
          showVehicleDetails: false,
        })
      );
    }
  }, [mapSearchboxOpen]);

  const MemoizedRefreshPlayPauseButton = useMemo(() => {
    return (
      <AdminFormFieldSubmit
        type="button"
        variant="primary"
        label={refreshPaused ? t("button_resume") : t("button_pause")}
        onClick={() => setRefreshPaused((state) => !state)}
        disabled={replayInProgress}
        buttonDimensionClass="h-[38px] w-28 px-4"
        // buttonTextClass="text-[10px] uppercase font-semibold"
      />
    );
  }, [refreshPaused, replayInProgress]);

  const inputStatusCodeDescriptions = useMemo(() => {
    if (!dataSingleVehicle?.custom_status_code_names) return {};
    return Object.fromEntries(
      dataSingleVehicle.custom_status_code_names.map((sc) => [
        sc.statuscode,
        sc.description || sc.statusname || "Unknown",
      ])
    );
  }, [dataSingleVehicle?.custom_status_code_names]);

  // hide streetview when user clicks anywhere outside map
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        mapState.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW &&
        mapWrapperRef.current &&
        !mapWrapperRef.current.contains(event.target)
      ) {
        gMapSetMode(
          mapRef.current.map,
          mapRef.current.map?.lastMapModeBeforeStreetview ||
            GOOGLE_MAP_MODE_NAMES.ROADMAP,
          mapRef.current.map.getCenter()
          // { skipEventListenerRegistration: true }
        );
      }
    };

    if (mapState?.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mapState?.mapType, mapWrapperRef, mapRef]);

  // route views checkboxes
  const [routeViewsVersion, bumpRouteViewsVersion] = useState(0);
  // if any of POINT_TO_POINT, SNAP_TO_ROAD or DIRECTIONS_SERVICE route views
  // are enabled, then disable the DEFAULT, and when none of the prior is enabled,
  // then enable the DEFAULT back
  // One rule, shared with the multi-vehicle map so the two can't drift:
  // the alternates stack, default is exclusive, and something is always on.
  const toggleRouteViewEnabled = useCallback((mode: RouteRenderMode) => {
    mapRef.current.routeViewsEnabled = toggleRouteView(
      mapRef.current.routeViewsEnabled,
      mode
    );
    bumpRouteViewsVersion((x) => x + 1);
  }, []);
  const RouteCheckboxes = useMemo(() => {
    return (
      <div className={`flex flex-col gap-2`}>
        <RouteCheckboxItem
          id="default"
          label={t("line_styles.default")}
          icon={svgIcons.LineStyleIconDefault}
          checked={mapRef.current?.routeViewsEnabled?.DEFAULT}
          // nothing ticked would draw no route at all, so default is the
          // floor. Disabled rather than silently snapping back on.
          disabled={routeViewIsLocked(
            mapRef.current.routeViewsEnabled,
            "DEFAULT"
          )}
          onChange={() => toggleRouteViewEnabled("DEFAULT")}
        />
        <div className="flex h-1 border-t border-t-accent-blue-pale" />
        <RouteCheckboxItem
          id="point-to-point"
          label={t("line_styles.point_to_point")}
          icon={svgIcons.LineStyleIconP2P}
          checked={mapRef.current?.routeViewsEnabled?.POINT_TO_POINT}
          onChange={() => toggleRouteViewEnabled("POINT_TO_POINT")}
        />
        <RouteCheckboxItem
          id="snap-to-road"
          label={t("line_styles.snap_to_roads")}
          icon={svgIcons.LineStyleIconSnap}
          checked={mapRef.current?.routeViewsEnabled?.SNAP_TO_ROAD}
          onChange={() => toggleRouteViewEnabled("SNAP_TO_ROAD")}
        />
        <RouteCheckboxItem
          id="directions-service"
          label={t("line_styles.directions_service")}
          icon={svgIcons.LineStyleIconDirSvc}
          checked={mapRef.current?.routeViewsEnabled?.DIRECTIONS_SERVICE}
          onChange={() => toggleRouteViewEnabled("DIRECTIONS_SERVICE")}
        />
      </div>
    );
  }, [routeViewsVersion, toggleRouteViewEnabled]);

  
  // trigger map updates on route view checkbox change
  useEffect(() => {
    // single-day only. mapUpdatesHandler calls .show() on the legacy
    // renderers, and getMapOpsProps changes on every zoom/pan - in
    // multi-day that would put the legacy routes back on the map.
    if (isMultiDay) return;
    mapUpdatesHandler(
      getMapOpsProps(),
      "routeViewsChange",
      mapRef.current.routeViewsEnabled
    );
  }, [routeViewsVersion, getMapOpsProps, isMultiDay]);

  // multi-day routes follow the same Default/Point-to-Point/Snap-to-Roads/
  // Directions-Service line style toggle as the single-day view, so the two
  // look consistent
  // Multi-day used to draw raw GPS fixes and only restyle the line when the
  // Route View changed, so the same trip sat off-road here and on-road in
  // single-day. It now runs the same resolver the multi-vehicle map does,
  // which means the Route View control finally changes geometry rather than
  // just the stroke.
  const multiDayResolvedPathsRef = useRef<Map<string, TimedPathPoint[]>>(
    new Map()
  );

  const multiDayRouteMode = useMemo(
    () => primaryRouteMode(mapRef.current?.routeViewsEnabled),
    // routeViewsVersion is bumped whenever the control writes to mapRef
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routeViewsVersion]
  );

  // the extra ticked views, drawn as comparison lines over the primary one
  const multiDayOverlayModes = useMemo(
    () => overlayRouteModes(mapRef.current?.routeViewsEnabled),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routeViewsVersion]
  );

  const multiDayTripsToResolve = useMemo(() => {
    if (!isMultiDay) return [];
    const trips: {
      tripKey: string;
      tripIndex: number;
      points: VehicleMapDataPoint[];
    }[] = [];
    dateGroups.forEach((group) => {
      group.trips.forEach((points, tripIndex) => {
        const tripKey = `${group.dateKey}__${tripIndex}`;
        if (!selectedTripKeys.has(tripKey)) return;
        if (points.length < 2) return;
        trips.push({ tripKey, tripIndex, points });
      });
    });
    return trips;
  }, [isMultiDay, dateGroups, selectedTripKeys]);

  const multiDayMapRender = useMultiDayMapRender({
    mapRef,
    dateGroups,
    resolvedPathsRef: multiDayResolvedPathsRef,
    selectedTripKeys,
    enabled: isMultiDay,
    isDataReady: isMapReady && !isFetchingMultiDay,
    mapInstanceVersion,
    routeViewsVersion,
    paused: multiDayReplay.isReplaying,
    hoveredTripKey: multiDayHoveredTrip?.tripKey ?? null,
    onMapHoverTrip: (tripKey) =>
      setMultiDayHoveredTrip(tripKey ? { tripKey, source: "map" } : null),
    includeNonTrips,
    detailedTripKey: multiDayDetailedTripKey,
    filteredStatusCodes,
    overlapsEnabled,
    onTripPointClick: handleMultiDayTripPointClick,
    assetKey: vehicleId,
    speedUnit: multiDaySpeedUnit,
    timezone: accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
  });

  // The map's highlight follows whatever is selected, wherever it was
  // picked - so clicking a row in the event list marks that pin on the map,
  // the mirror of clicking a pin scrolling the list to its row.
  //
  // Nulls are ignored here on purpose: picking an event flushes
  // selectedTripPoint to null for 100ms before the new point lands, and
  // acting on that would blink the highlight off between every pick.
  useEffect(() => {
    if (selectedTripPoint) multiDayMapRender.highlightEventPoint(selectedTripPoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTripPoint]);

  // Clearing comes off the popup closing instead, which has several ways in
  // (its own close, the filter modal's reset, backing out of the trip).
  const eventPopupIsOpen = !!modalsState?.showVehicleDetails;
  useEffect(() => {
    if (!eventPopupIsOpen) multiDayMapRender.highlightEventPoint(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventPopupIsOpen]);

  // resolves each trip's road-aligned path in the background and swaps it
  // in as it lands, so the map goes up immediately on the raw fixes
  useResolvedRoutePaths({
    trips: multiDayTripsToResolve,
    mode: multiDayRouteMode,
    pathsRef: multiDayResolvedPathsRef,
    onResolved: multiDayMapRender.applyResolvedPath,
  });

  // and the same again for whatever extra views are ticked, which draw as
  // comparison lines rather than replacing the one above
  useRouteOverlayPaths({
    trips: multiDayTripsToResolve,
    modes: multiDayOverlayModes,
    onResolved: multiDayMapRender.applyOverlayPath,
  });

  // A trip is open and nothing is playing over it. The controls panel stays
  // up on the finished route instead of vanishing, so the trip can be
  // replayed from the same place it was watched - and at the speed picked
  // there, since those buttons stay on screen too.
  const multiDayDetailFinished =
    !!multiDayDetailedTripKey &&
    // the comparison grid covers the map, so nothing belongs on top of it
    !isComparing &&
    !multiDayReplay.isReplaying &&
    !multiDayMapRender.isAnimating &&
    !multiDayMapRender.isDetailAnimating &&
    multiDayMapRender.detailDrawComplete;

  // the universal replay clock: whichever multi-day animation currently has
  // the map, in the account's timezone. pausing freezes the underlying
  // state, so the displayed time freezes with it.
  const multiDayReplayClock = multiDayReplay.isReplaying
    ? multiDayReplay.replayClock
    // isAnimating, not isReplayingAll: the initial reveal reports onto the
    // same clock, and it is an animation the panel now sits over
    : multiDayMapRender.isAnimating
      ? multiDayMapRender.replayClock
      : multiDayMapRender.detailClock;
  // the date leads here, unlike the multi-vehicle map's clock: this view
  // can hold up to 7 days at once, so a bare time says nothing about which
  // day's 8am you're watching.
  const multiDayFormattedClock =
    multiDayReplayClock != null
      ? formattedDateTimeBasedOnTimezone(
          dbTimeToJsTime(multiDayReplayClock),
          accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE,
          selectedDates.length > 1 ? "LLL dd · h:mm:ss aa" : "h:mm:ss aa"
        )
      : null;

  // Reset the route view when moving between the trip list and a trip's
  // details, so a choice made inside one trip doesn't silently carry into
  // the next.
  //
  // Keyed on multiDayDetailedTripKey as well as selectedTrip: selectedTrip
  // is the legacy single-day index, and multi-day is now the normal path,
  // so on its own this ran once at mount and never again - leaving whatever
  // was last picked in force for every trip after it.
  useEffect(() => {
    // reset showRouteViewsPopup to false
    setShowRouteViewsPopup(false);
    // reset mapRef.current.routeViewsEnabled to default active and rest inactive
    if (mapRef.current) {
      mapRef.current.routeViewsEnabled = {
        DEFAULT: true,
        POINT_TO_POINT: false,
        SNAP_TO_ROAD: false,
        DIRECTIONS_SERVICE: false,
      };
      bumpRouteViewsVersion((x) => x + 1);
    }
  }, [selectedTrip, multiDayDetailedTripKey]);

  return (
    <>
      {!!buzzerAlertTimestamp && (
        <HeaderView
          title={tBuzzerAlert("heading")}
          showBackButton={true}
          backButtonCallback={() =>
            navigate(routeUrls.dashboardChildren.panic_alert)
          }
        />
      )}
      <div
        className={
          !!crashTrackEventData
            ? "relative map-asset-wrapper"
            : `${APP_CONFIG.DES.DASH.P_HORIZ} py-2`
        }
      >
        {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
          <pre className="text-[8px] absolute top-24 right-5 z-map-popup pointer-events-none">
            {JSON.stringify(mapState, null, 2)}
          </pre>
        )}
        <div
          className={`flex flex-col md:flex-row rounded-lg ${
            buzzerAlertTimestamp
              ? userIsImpersonated
                ? "h-[calc(100vh-10rem-40px)]"
                : "h-[calc(100vh-10rem)]"
              : !!crashTrackEventData
                ? userIsImpersonated
                  ? "h-[calc(100vh-16rem-40px)]"
                  : "h-[calc(100vh-16rem)]"
                : userIsImpersonated
                  ? "h-[calc(100vh-7rem-40px)]"
                  : "h-[calc(100vh-7rem)]"
          } overflow-hidden relative`}
        >
          {/* h-[60%] rather than a vh: on small screens this and the map
              below are stacked inside a parent of a fixed pixel height, and
              55vh + 45vh comes to a full viewport - taller than that parent,
              which clips the bottom of whichever one is last. Percentages
              are of the parent, so the two always add up. min-h-0 lets the
              list inside actually scroll: a flex item defaults to
              min-height:auto, which refuses to shrink below its content, so
              an overflow-auto child never gets a scrollbar - it just
              overflows and gets clipped. */}
          <div
            className={`flex flex-col w-full md:w-72 h-[60%] md:h-full min-h-0 order-2 md:order-1${
              // hidden xl:block
              isFetchingOrgVehicles ? " opacity-40 pointer-events-none" : ""
            } ${crashTrackEventData ? "bg-white" : "bg-gray-100"}`}
          >
            {selectedTrip !== null ? (
              <>
                {!!buzzerAlertTimestamp ? (
                  <div className="flex flex-col gap-4 px-4 mt-4">
                    <h2 className="font-bold text-xl">
                      {t("tripDetails.heading")}
                    </h2>
                    <MapVehicleListingColumnItemForBuzzerAlert
                      asideText={
                        <span
                          className={`flex items-center justify-end gap-1 leading-3 text-[10px] ${
                            vehicleOrientation > 0 ||
                            mapVehicleIsActive(vehicleDataPoint)
                              ? "text-field-success"
                              : "text-field-error-dark"
                          }`}
                        >
                          {tMapFleet(
                            vehicleOrientation > 0
                              ? "vehicleStatus.driving"
                              : mapVehicleIsActive(vehicleDataPoint)
                                ? "vehicleStatus.idle_active"
                                : "vehicleStatus.idle_inactive"
                          )}
                          <div
                            dangerouslySetInnerHTML={{
                              __html:
                                vehicleOrientation > 0
                                  ? mapVehicleIconWrapped(
                                      "driving",
                                      false,
                                      vehicleOrientation
                                    )
                                  : mapVehicleIconWrapped(
                                      mapVehicleStateIconSlugFromMapOverview(
                                        vehicleDataPoint
                                      )
                                    ),
                            }}
                          />
                        </span>
                      }
                      bottomText={
                        vehicleDataPoint.licenseplate ||
                        vehicleDataPoint.equipmenttype
                      }
                      description={
                        vehicleDataPoint.displayname ||
                        vehicleDataPoint.equipmenttype
                      }
                      title={mapVehicleDisplayTitle(vehicleDataPoint)}
                    />
                  </div>
                ) : (
                  <div
                    className={`flex gap-4 px-4${!!crashTrackEventData ? "" : " mt-4"}`}
                  >
                    <div>
                      {!crashTrackEventData && (
                        <HeaderView
                          title={t("tripDetails.heading")}
                          showBackButton={true}
                          backButtonCallback={() => setSelectedTrip(null)}
                          wrapperClassName={`flex justify-between py-2`}
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Vehicle selection */}
                <div className="px-4 mt-4 z-[11]">
                  {/* <pre className="text-xs h-24 overflow-auto">
                    {JSON.stringify(dataSingleVehicle, null, 2)}
                  </pre> */}
                  {isFetchingOrgVehicles ||
                  isFetchingSingleVehicle ||
                  // || isFetchingOrgVehiclesEvents
                  isFetchingVehicleMapData ||
                  mapStateTransitionInProgress ||
                  !dataSingleVehicle ? (
                    <PseudoSelect label={t("selected_vehicle")} />
                  ) : (
                    <>
                      <AdminFormFieldAsyncLazyDropdown
                        loadingData={isFetchingSearchedVehicles}
                        label={t("selected_vehicle")}
                        id="selected_vehicle"
                        name="selected_vehicle"
                        noOptionsMessage={() =>
                          t("selected_vehicle_no_options")
                        }
                        defaultOption={vehicleDropdownOption(
                          dataSingleVehicle as TLegacyVehicle
                        )}
                        // defaultOptions={dataOrgVehicles?.results?.map(
                        //   (vehicleItem) => vehicleDropdownOption(vehicleItem)
                        // )}
                        additional={{ page: 1 }}
                        loadOptions={
                          debouncedLoadOptionsHandlerVehicleAutosuggest
                        }
                        onChange={handleSelectedVehicleChange}
                        detailsFormField={true}
                        menuPlacement={
                          mobileHelpers.viewportIsMobile ? "top" : "auto"
                        }
                        maxMenuHeight={
                          mobileHelpers.viewportIsMobile ? 240 : undefined
                        }
                        key={`vehicle-select-${dataSingleVehicle?.deviceid}-${JSON.stringify(mobileHelpers.viewportIsMobile)}`}
                      />
                    </>
                  )}
                </div>

                {/* Datepicker with prev next buttons - single-day only.
                    It steps tripFilters.selected_date one day at a time,
                    which in multi-day is neither what the view is showing
                    nor even inside the selected range: the range picker
                    below owns the dates there, and this sits above it
                    displaying an unrelated one. */}
                {!isMultiDay && (
                  <div
                    className={`px-4 mt-4 flex gap-2 ${mobileHelpers.viewportIsMobile ? "z-[12]" : "z-10"}`}
                  >
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={tMain("prev")}
                      onClick={() => {
                        // change trip filter date 1 day back
                        // const newFromDate = new Date(tripFilters.date_from);
                        // const newToDate = new Date(tripFilters.date_to);
                        // const { calculatedUnixTimestampFrom, calculatedUnixTimestampTo }
                        //   = computedUnixTimestamps(
                        //     newFromDate.toISOString().split('T')[0],
                        //     tripFilters.time_from,
                        //     newToDate.toISOString().split('T')[0],
                        //     tripFilters.time_to,
                        //     tripFilters.timezone
                        //   );
                        // setTripFilters({
                        //   ...tripFilters,
                        //   date_from: !!tripFilters.date_from
                        //     ? new Date(new Date(tripFilters.date_from).getTime() - 86400000).toISOString().split('T')[0]
                        //     : tripFilters.date_from,
                        //   date_to: !!tripFilters.date_to
                        //     ? new Date(new Date(tripFilters.date_to).getTime() - 86400000).toISOString().split('T')[0]
                        //     : tripFilters.date_to,
                        //   calculatedUnixTimestampFrom,
                        //   calculatedUnixTimestampTo,
                        // });
                        if (!tripFilters.selected_date) return;
                        const newSelectedDate = new Date(
                          tripFilters.selected_date
                        );
                        newSelectedDate.setDate(newSelectedDate.getDate() - 1);
                        const newFilterData = calculatedTimeFilters(
                          newSelectedDate.toISOString().split("T")[0],
                          tripFilters.timezone
                        );
                        setTripFilters(newFilterData);
                      }}
                      disabled={selectedTrip === 0}
                      buttonDimensionClass="h-8 px-2"
                      buttonTextClass="text-[10px] uppercase font-semibold"
                    />
                    {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
                      <pre className="absolute -mt-4 text-xs">
                        {fromZonedTime(
                          !!tripFilters.calculatedUnixTimestampFrom
                            ? dbTimeToJsTime(
                                tripFilters.calculatedUnixTimestampFrom
                              )
                            : 0,
                          tzForDateFns
                        ).toISOString()}
                      </pre>
                    )}
                    <div className="relative w-full h-8">
                      <AdminFormFieldInput
                        customWrapperClass="absolute top-0 left-0 w-full h-full z-10 peer"
                        basicInputClass="w-full h-8 rounded-md text-xs shadow-sm opacity-0 cursor-pointer"
                        customInputClass="p-1"
                        calendarIconDimensionClass="right-0 top-0 size-4 hidden"
                        innerWrapperClass="mt-0 relative flex w-full"
                        label={""}
                        type="date-picker"
                        id={`date_time`}
                        name={`date_time`}
                        value={
                          !!tripFilters.calculatedUnixTimestampFrom
                            ? dbTimeToJsTime(
                                tripFilters.calculatedUnixTimestampFrom
                              )
                            : undefined
                        }
                        maxTime={new Date()}
                        onDatepickerChange={(fieldName: string, value: any) => {
                          // const selectedDate = new Date(value);
                          // const nextDate = new Date(selectedDate.getTime() + 86400000);
                          // const { calculatedUnixTimestampFrom, calculatedUnixTimestampTo }
                          //   = computedUnixTimestamps(
                          //     selectedDate.toISOString().split('T')[0],
                          //     tripFilters.time_from,
                          //     nextDate.toISOString().split('T')[0],
                          //     tripFilters.time_to,
                          //     tripFilters.timezone
                          //   );
                          // setTripFilters({
                          //   ...tripFilters,
                          //   date_from: selectedDate.toISOString().split('T')[0],
                          //   date_to: nextDate.toISOString().split('T')[0],
                          //   calculatedUnixTimestampFrom,
                          //   calculatedUnixTimestampTo,
                          // });
                          const newFilterData = calculatedTimeFilters(
                            new Date(value).toISOString().split("T")[0],
                            tripFilters.timezone
                          );
                          setTripFilters(newFilterData);
                        }}
                        // onBlur={handleBlur}
                        // error={errors.date_time}
                        // touched={touched.date_time}
                        placement={
                          mobileHelpers.viewportIsMobile ? "top" : "bottom"
                        }
                      />
                      <span className="absolute top-0 left-0 w-full h-full z-0 text-xs flex items-center justify-center font-semibold text-center bg-accent-blue-pale peer-hover:bg-accent-blue-dark text-accent-blue-dark peer-hover:text-white rounded-lg pointer-events-none touch-none">
                        {/* tripFilters.date_from in MM/DD/YYYY format */}
                        {formattedDateTimeBasedOnTimezone(
                          tripFilters.selected_date ?? tripFilters.date_from,
                          tzForDateFns,
                          "MM/dd/yyyy"
                        )}
                      </span>
                    </div>
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={tMain("next")}
                      onClick={() => {
                        // change trip filter date 1 day forward but not beyond yesterday
                        // const newFromDate = new Date(tripFilters.date_from);
                        // const newToDate = new Date(tripFilters.date_to);
                        // const todayDate = new Date();
                        // if (newFromDate.getTime() >= todayDate.getTime() - 86400000 * 2) return;
                        // if (newToDate.getTime() >= todayDate.getTime() - 86400000 * 2) return;
                        // const { calculatedUnixTimestampFrom, calculatedUnixTimestampTo }
                        //   = computedUnixTimestamps(
                        //     newFromDate.toISOString().split('T')[0],
                        //     tripFilters.time_from,
                        //     newToDate.toISOString().split('T')[0],
                        //     tripFilters.time_to,
                        //     tripFilters.timezone
                        //   );
                        // setTripFilters({
                        //   ...tripFilters,
                        //   date_from: !!tripFilters.date_from
                        //     ? new Date(new Date(tripFilters.date_from).getTime() + 86400000).toISOString().split('T')[0]
                        //     : tripFilters.date_from,
                        //   date_to: !!tripFilters.date_to
                        //     ? new Date(new Date(tripFilters.date_to).getTime() + 86400000).toISOString().split('T')[0]
                        //     : tripFilters.date_to,
                        //   calculatedUnixTimestampFrom,
                        //   calculatedUnixTimestampTo,
                        // });
                        if (!tripFilters.selected_date) return;
                        const newSelectedDate = new Date(
                          tripFilters.selected_date
                        );
                        newSelectedDate.setDate(newSelectedDate.getDate() + 1);
                        const newFilterData = calculatedTimeFilters(
                          newSelectedDate.toISOString().split("T")[0],
                          tripFilters.timezone
                        );
                        setTripFilters(newFilterData);
                      }}
                      disabled={selectedTrip === 0}
                      buttonDimensionClass="h-8 px-2"
                      buttonTextClass="text-[10px] uppercase font-semibold"
                    />
                  </div>
                )}

                {/* Filters section */}
                <div className="flex justify-between px-4 pt-4 items-center">
                  <p className="font-medium text-lg leading-6">Trips</p>
                  <div className="flex gap-6 relative">
                    {/* the trip filter is a single-day control - it filters
                        one day's trips by time and event. multi-day picks
                        its trips with the date selector and the per-day
                        checkboxes instead, so the icon would open a panel
                        that can't act on what's on screen. */}
                    {!isMultiDay && (
                      <button
                        type="button"
                        onClick={() => {
                          dispatch(
                            setModalsData({
                              ...modalsState,
                              showLayerFilter: false,
                              showEventFilter: false,
                              showVehicleDetails: false,
                              showVehicleFilter: true,
                            })
                          );
                        }}
                        disabled={
                          isFetchingOrgVehicles ||
                          isFetchingSingleVehicle /* || isFetchingOrgVehiclesEvents*/ ||
                          isFetchingVehicleMapData
                        }
                      >
                        <img
                          src={svgIcons.FilterIcon}
                          alt="filter-icon"
                          className={
                            isFetchingOrgVehicles ||
                            isFetchingSingleVehicle /* || isFetchingOrgVehiclesEvents*/ ||
                            isFetchingVehicleMapData
                              ? "grayscale opacity-70"
                              : ""
                          }
                        />
                      </button>
                    )}
                    <button
                      className={`p-1 cursor-pointer${showSortingDropdown && " bg-white rounded-lg relative z-modal"}`}
                      type="button"
                      onClick={() =>
                        setShowSortingDropdown(!showSortingDropdown)
                      }
                      disabled={
                        isFetchingOrgVehicles ||
                        isFetchingSingleVehicle /* || isFetchingOrgVehiclesEvents*/ ||
                        isFetchingVehicleMapData
                      }
                    >
                      <img
                        src={svgIcons.SortIcon}
                        alt="sort-icon"
                        className={
                          isFetchingOrgVehicles ||
                          isFetchingSingleVehicle /* || isFetchingOrgVehiclesEvents*/ ||
                          isFetchingVehicleMapData
                            ? "grayscale opacity-70"
                            : ""
                        }
                      />
                    </button>
                    <SortingFilter
                      showSortingDropdown={showSortingDropdown}
                      setShowSortingDropdown={setShowSortingDropdown}
                      sortingOptions={VEHICLE_MAP_SORTING_OPTIONS}
                      selectedSorting={
                        isMultiDay ? multiDaySorting : selectedSorting
                      }
                      setSelectedSorting={(item) => {
                        if (isMultiDay) setMultiDaySorting(item);
                        else setSelectedSorting(item);
                        setShowSortingDropdown(false);
                      }}
                    />
                  </div>
                </div>
                <ModalVehicleTripFilters
                  loadingData={
                    isFetchingOrgVehicles ||
                    isFetchingSingleVehicle /* || isFetchingOrgVehiclesEvents*/ ||
                    isFetchingVehicleMapData
                  }
                  savedFilterValues={tripFilters}
                  onFilterChange={(filterData: VehicleTripFiltersData) => {
                    if (
                      filterData.selected_date === tripFilters.selected_date &&
                      // fields date_from, date_to, time_from, time_to are not checked here anymore
                      // because we rely on selected_date only for computing these values
                      // --------------------------------------------------------------------------
                      // filterData.date_from === tripFilters.date_from
                      // && filterData.date_to === tripFilters.date_to
                      // && filterData.time_from === tripFilters.time_from
                      // && filterData.time_to === tripFilters.time_to
                      filterData.timezone === tripFilters.timezone
                    ) {
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showVehicleFilter: false,
                        })
                      );
                      console.log("close modal");
                      return;
                    }
                    // let calculatedUnixTimestampFrom = null;
                    // let calculatedUnixTimestampTo = null;
                    // if (!!filterData.date_from && !!filterData.time_from && !!filterData.timezone) {
                    //   const selectedDate = `${filterData.date_from} ${filterData.time_from}` // :00
                    //   const utcDate = fromZonedTime(selectedDate, filterData.timezone);
                    //   const jsTime = new Date(utcDate).getTime();
                    //   if (!!jsTime && !isNaN(jsTime)) {
                    //     calculatedUnixTimestampFrom = jsTime / 1000;
                    //     if (APP_CONFIG.DEBUG.ALL) console.log('unixTimestampFrom', calculatedUnixTimestampFrom);
                    //   } else {
                    //     if (APP_CONFIG.DEBUG.ALL) console.error('Invalid datetime (from)', jsTime);
                    //   }
                    // }
                    // if (!!filterData.date_to && !!filterData.time_to && !!filterData.timezone) {
                    //   const selectedDate = `${filterData.date_to} ${filterData.time_to}` // :00
                    //   const utcDate = fromZonedTime(selectedDate, filterData.timezone);
                    //   const jsTime = new Date(utcDate).getTime();
                    //   if (!!jsTime && !isNaN(jsTime)) {
                    //     calculatedUnixTimestampTo = jsTime / 1000;
                    //     if (APP_CONFIG.DEBUG.ALL) console.log('unixTimestampTo', calculatedUnixTimestampTo);
                    //   } else {
                    //     if (APP_CONFIG.DEBUG.ALL) console.error('Invalid datetime (to)', jsTime);
                    //   }
                    // }
                    if (!filterData.selected_date) return;
                    const newFilterData = calculatedTimeFilters(
                      filterData.selected_date,
                      convertToTZForDateFns(filterData.timezone)
                    );
                    setTripFilters(newFilterData);
                    // update url with new filter values
                    navigate(
                      `${routeUrls.dashboardChildren.mapsChildren.getVehicleMapUri(
                        getVehicleUUID({
                          accountid: `${thisUserOrganizationId}`,
                          deviceid: dataSingleVehicle?.deviceid,
                          vehicleid: dataSingleVehicle?.vehicleid,
                        }),
                        `${newFilterData.calculatedUnixTimestampFrom}`,
                        `${newFilterData.calculatedUnixTimestampTo}`
                      )}`
                    );
                  }}
                />
              </>
            )}

            {/* Asset details on single trip details view */}
            {selectedTrip !== null && ( // !!crashTrackEventData &&
              <>
                <div
                  className={`mt-4${!!crashTrackEventData ? " mb-2" : ""} mx-3 flex items-start text-accent-blue-dark`}
                >
                  <img
                    className="bg-accent-blue-pale rounded-lg px-2 size-10"
                    src={dashboardMenuIcons.admins.vehicle}
                    alt=""
                  />
                  <div className="flex flex-col gap-2 ms-4">
                    <h3 className="font-bold text-base leading-4">
                      {dataSingleVehicle?.deviceid}
                    </h3>
                    <h4 className="text-xs">
                      {Array.from(
                        new Set([
                          dataSingleVehicle?.description,
                          dataSingleVehicle?.displayname,
                        ] as string[])
                      )
                        .filter((item) => !!item)
                        .join(" | ")}
                    </h4>
                  </div>
                </div>
                {/* <div className="mt-2 mx-3">{RouteCheckboxes}</div> */}
              </>
            )}

            {isMultiDay && multiDayDetailedTripKey ? (
              // a trip is open - the panel shows its full Trip Details
              <TripDetailPanel
                dateGroups={dateGroups}
                tripKey={multiDayDetailedTripKey}
                vehicleDeviceId={dataSingleVehicle?.deviceid}
                vehicleSubtitle={Array.from(
                  new Set([
                    dataSingleVehicle?.description,
                    dataSingleVehicle?.displayname,
                  ] as string[])
                )
                  .filter((item) => !!item)
                  .join(" | ")}
                assignedDriver={
                  !vehicleId || !!isErrorAssignedDriver
                    ? undefined
                    : dataAssignedDriver
                }
                selectedTripPoint={selectedTripPoint}
                setSelectedTripPoint={setSelectedTripPoint}
                onBack={() => setMultiDayDetailedTripKey(null)}
                onReplayEvents={() =>
                  startMultiDayReplayEvents(multiDayDetailedTripKey)
                }
                replayInProgress={
                  multiDayReplay.isReplaying || multiDayMapRender.isAnimating
                }
                replayPercentage={
                  multiDayReplay.replayingTripKey === multiDayDetailedTripKey
                    ? multiDayReplay.replayPercentage
                    : null
                }
              />
            ) : isMultiDay ? (
              <>
                <DateSelector
                  selectedDates={selectedDates}
                  onChange={setSelectedDates}
                />
                {isFetchingMultiDay ? (
                  <div className="flex-grow flex items-center justify-center pb-12">
                    <h4 className="font-bold text-lg text-gray-400">
                      {t("loading_vehicle_data")}
                    </h4>
                  </div>
                ) : (
                  <TripListMultiDay
                    dateGroups={dateGroups}
                    selectedTripKeys={selectedTripKeys}
                    onToggleTripKey={(key, isChecked) => {
                      setSelectedTripKeys((prev) => {
                        const next = new Set(prev);
                        if (isChecked) next.add(key);
                        else next.delete(key);
                        return next;
                      });
                    }}
                    onSetAllSelected={(isChecked) => {
                      setSelectedTripKeys(() => {
                        const next = new Set<string>();
                        if (isChecked) {
                          dateGroups.forEach((group) =>
                            group.trips.forEach((points, tripIndex) => {
                              if (!includeNonTrips && isNonTrip(points)) return;
                              next.add(tripKeyFor(group.dateKey, tripIndex));
                            })
                          );
                        }
                        return next;
                      });
                    }}
                    assignedDriver={
                      !vehicleId || !!isErrorAssignedDriver
                        ? undefined
                        : dataAssignedDriver
                    }
                    replayingTripKey={multiDayReplay.replayingTripKey}
                    replayPercentage={multiDayReplay.replayPercentage}
                    isReplaying={
                      multiDayReplay.isReplaying || multiDayMapRender.isAnimating
                    }
                    onReplayEvents={startMultiDayReplayEvents}
                    onReplayAll={startMultiDayReplayAll}
                    hoveredTripKey={multiDayHoveredTrip?.tripKey ?? null}
                    onHoverTrip={(tripKey) =>
                      setMultiDayHoveredTrip(
                        tripKey ? { tripKey, source: "list" } : null
                      )
                    }
                    autoScrollOnHover={multiDayHoveredTrip?.source === "map"}
                    sortOrder={multiDaySorting}
                    includeNonTrips={includeNonTrips}
                    onToggleIncludeNonTrips={setIncludeNonTrips}
                    detailedTripKey={multiDayDetailedTripKey}
                    onSetDetailedTrip={openMultiDayTripDetail}
                    selectedTripPoint={selectedTripPoint}
                    setSelectedTripPoint={setSelectedTripPoint}
                  />
                )}
              </>
            ) : (
              <>
                <DateSelector
                  selectedDates={selectedDates}
                  onChange={(dates) => {
                    // clear any lingering legacy route right away instead
                    // of leaving it on screen while the new selection's data
                    // is still being fetched
                    if (dates.length > 0) clearLegacyMapLayer(mapRef);
                    setSelectedDates(dates);
                  }}
                />
                {/* Trips list */}
                <div
                  className={`${!!crashTrackEventData ? "" : "mt-4 "}flex-grow min-h-0 flex flex-col overflow-y-auto overscroll-contain${replayInProgress ? " pointer-events-none touch-none" : ""}`}
                >
              {/* selectedSorting === "desc" ? "flex-col-reverse justify-end" : "flex-col justify-start" */}
              {isFetchingOrgVehicles ||
              isFetchingSingleVehicle ||
              // isFetchingOrgVehiclesEvents ||
              (isFetchingVehicleMapData &&
                !dataVehicleMapData &&
                !selectedTrip) ? (
                <div className="flex-grow flex items-center justify-center pb-12">
                  <h4 className="font-bold text-lg text-gray-400">
                    {t("loading_vehicle_data")}
                  </h4>
                </div>
              ) : (
                <>
                  {
                    /* {!!dataSingleVehicle?.lasteventtimestamp && */
                    dataSingleVehicle?.lasteventtimestamp &&
                    !errorVehicleMapData &&
                    !!dataVehicleMapData &&
                    sanitizedVehicleMapData.length > 0 ? (
                      <>
                        {sanitizedVehicleMapData.map((trip, index) => (
                          <React.Fragment key={`parent-${index}`}>
                            {selectedTrip !== null && selectedTrip !== index ? (
                              <></>
                            ) : (
                              <TripDetails                                
                                assignedDriver={
                                  // error in response implies that driver is not assigned
                                  !vehicleId || !!isErrorAssignedDriver
                                    ? undefined
                                    : dataAssignedDriver
                                }
                                key={index}
                                detailedView={selectedTrip === index}
                                onClick={() => {
                                  if (clearSearchRef.current) {
                                    clearSearchRef.current(true);
                                  }
                                  if (selectedTrip === index) {
                                    handleTripSelection(null);
                                    // setRefreshPaused(false); // do not start refresh initially
                                  } else {
                                    handleTripSelection(index);
                                  }
                                }}
                                onMouseOver={() => {
                                  if (APP_CONFIG.DEBUG.MAPS)
                                    console.log("INDEX", index);
                                  setHoveredTrip(index);
                                }}
                                onMouseOut={() => {
                                  setHoveredTrip(null);
                                }}
                                onReplayEvents={() => {
                                  if (APP_CONFIG.DEBUG.MAPS)
                                    console.log("INDEX", index);
                                  onTripReplayEvents(
                                    sanitizedVehicleMapData.length - index
                                  );
                                }}
                                replayInProgress={replayInProgress}
                                replayPercentage={replayPercentage}
                                tripNumber={
                                  singleDayTripNumbers.get(index) ?? null
                                }
                                tripPoints={trip}
                                selectedTripPoint={selectedTripPoint}
                                setSelectedTripPoint={(point) => {
                                  if (clearSearchRef.current) {
                                    clearSearchRef.current(true);
                                  }

                                  setSelectedTripPoint(point);
                                }}
                                mapRef={mapRef}
                                crashTrackEventData={crashTrackEventData}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </>
                    ) : (
                      <div className="flex-grow flex items-center justify-center pb-12">
                        <h4 className="font-bold text-lg text-gray-400">
                          {isFetchingVehicleMapData || isFetchingSingleVehicle
                            ? t("loading_vehicle_data")
                            : t("no_trip_data")}
                        </h4>
                      </div>
                    )
                  }
                </>
              )}
                </div>
              </>
            )}
          </div>

          <div
            className="h-[40%] md:h-full md:flex-grow min-h-0 relative vehicle-map-wrapper order-1 md:order-2"
            ref={mapWrapperRef}
          >
            {isComparing && (
              <TripCompareView
                dateGroups={dateGroups}
                tripKeys={compareTripKeys}
                timezone={accountData?.timezone || APP_CONFIG.DEFAULT_TIMEZONE}
                statusCodeDescriptions={inputStatusCodeDescriptions}
                initialFilteredStatusCodes={
                  mapPinsDisabled ? IGNITION_STATUS_CODES : filteredStatusCodes
                }
                routeMode={multiDayRouteMode}
                onBack={() => setIsComparing(false)}
                onManageSelection={() => setIsCompareModalOpen(true)}
                setSelectedTripPoint={setSelectedTripPoint}
              />
            )}

            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <div className="absolute top-0 left-0 px-2 bg-[#FFFFFFAA] text-field-error-dark text-[10px] font-bold uppercase z-10">
                mapType: {mapState?.mapType}
              </div>
            )}
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <pre
                className={`absolute ${refreshOptionsExpanded ? "top-56 mt-4" : "top-32"} left-5 px-2 h-[540px] overflow-auto bg-[#FFFFFFAA] p-2 mt-1 text-[10px] z-map-popup Xpointer-events-none`}
              >
                mapScriptLoaded:{" "}
                <span
                  className={
                    mapState?.mapScriptLoaded !== true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {mapState?.mapScriptLoaded ? "true" : "false"}
                </span>
                <br />
                mapData.ready:{" "}
                <span
                  className={
                    mapState?.mapData?.ready !== true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {mapState?.mapData?.ready ? "true" : "false"}
                </span>{" "}
                (ignored)
                <br />
                isFetchingOrgGeozones:{" "}
                <span
                  className={
                    isFetchingOrgGeozones === true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {isFetchingOrgGeozones ? "true" : "false"}
                </span>
                <br />
                isFetchingOrgVehicles:{" "}
                <span
                  className={
                    isFetchingOrgVehicles === true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {isFetchingOrgVehicles ? "true" : "false"}
                </span>
                <br />
                {/* isFetchingOrgVehiclesEvents: {isFetchingOrgVehiclesEvents ? 'true' : 'false'}<br /> */}
                isFetchingVehicleMapData:{" "}
                <span
                  className={
                    isFetchingVehicleMapData === true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {isFetchingVehicleMapData ? "true" : "false"}
                </span>
                <br />
                mapStateTransitionInProgress:{" "}
                <span
                  className={
                    mapStateTransitionInProgress === true
                      ? "text-field-error-dark"
                      : ""
                  }
                >
                  {mapStateTransitionInProgress ? "true" : "false"}
                </span>
                <br />
                mapState?.mapCenter:{" "}
                {JSON.stringify(mapState?.mapCenter, null, 2)}
                <br />
                ---------------------------------
                <br />
                refreshPaused: {JSON.stringify(refreshPaused, null, 2)}
                <br />
                ---------------------------------
                <br />
                userMapActivity: {JSON.stringify(userMapActivity, null, 2)}
                <br />
                ---------------------------------
                <br />
                hideMapNearRefresh:{" "}
                {JSON.stringify(hideMapNearRefresh, null, 2)}
                <br />
                userMapRefreshing: {JSON.stringify(userMapRefreshing, null, 2)}
                <br />
                ---------------------------------
                <br />
                userMapSelection: {JSON.stringify(userMapSelection, null, 2)}
                <br />
                ---------------------------------
                <br />
                <br />
                <br />
                tripFilters:
                <br />
                {JSON.stringify(tripFilters, null, 2)}
                <br />
                ---------------------------------
                <br />
                routeViewsEnabled:{" "}
                {JSON.stringify(mapRef.current.routeViewsEnabled, null, 2)}
                <br />
                ---------------------------------
              </pre>
            )}

            {/* Moved MapTopRightControls out of the render conditions (below) to help retain searched location in searchbox */}
            {/* These sit at z-[200], above the compare view that covers the
                map at z-[80], so search / overlay / layers / zoom float over
                a screen none of them can act on. Hidden with a class rather
                than unmounted: keeping it mounted is the whole point of the
                note above - unmounting throws away whatever was searched. */}
            <div className={isComparing ? "hidden" : ""}>
              <MapTopRightControls
                mapRef={mapRef as React.MutableRefObject<TMapRef>}
                onGAutocompleteClearCallback={() => {
                  // reset map to default view
                  mapUpdatesHandler(
                    getMapOpsProps(),
                    "centerToPushpin",
                    sanitizedVehicleMapData
                  );
                }}
                onSearchboxToggle={setMapSearchboxOpen}
                onDirectionChange={handleDirectionsChange}
                clearSearchRef={clearSearchRef}
              />
            </div>

            
            {!!mapState?.mapScriptLoaded && // mapState?.mapData?.ready
            !isLoadingOrgGeozones &&
            !isLoadingOrgVehicles &&
            // !isFetchingOrgVehiclesEvents &&
            !isFetchingVehicleMapData &&
            !mapStateTransitionInProgress ? (
              <>
                <BasicMap
                  className={`bg-gray-200 h-full w-full${mapSearchboxOpen ? " map-searchbox-open" : ""}`}
                  mapRef={mapRef}
                  mapData={mapState?.mapData}
                  recordActivityAsPage="assetMap"
                  nativeMapControls
                  // setMapData={() => {}}
                  onMapReady={handleMapReady}
                />

                {/* multi-day legend */}
                {isMultiDay &&
                  !multiDayReplay.isReplaying &&
                  !multiDayMapRender.isReplayingAll &&
                  dateGroups.length > 0 && (
                    <div className="absolute bottom-5 left-5 z-[60] bg-white rounded-lg shadow-md p-3 text-xs max-w-[200px]">
                      <div className="flex flex-col gap-1">
                        {dateGroups.map((group) => {
                          const visibleCount = includeNonTrips
                            ? group.trips.length
                            : group.trips.filter((points) => !isNonTrip(points))
                                .length;
                          return (
                            <div
                              key={group.dateKey}
                              className="flex items-center gap-2"
                            >
                              <span
                                className="inline-block size-3 rounded-full shrink-0"
                                style={{ backgroundColor: group.color }}
                              />
                              <span>{group.label}</span>
                              <span className="text-gray-400">
                                &middot; {visibleCount} trip
                                {visibleCount === 1 ? "" : "s"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                {/* shared controls for replay events, replay all and the
                    detail-view route draw */}
                {(multiDayReplay.isReplaying ||
                  multiDayMapRender.isAnimating ||
                  multiDayMapRender.isDetailAnimating ||
                  multiDayDetailFinished) && (
                  <div className="absolute top-5 left-1/2 -translate-x-1/2 z-[60]">
                    <ReplayControlsPanel
                      label={
                        multiDayReplay.isReplaying
                          ? "Replaying trip"
                          : multiDayMapRender.isReplayingAll
                            ? "Replaying trips"
                            : multiDayMapRender.isDetailAnimating
                              ? "Drawing route"
                              : multiDayDetailFinished
                                ? "Trip route"
                                : "Drawing routes"
                      }
                      isFinished={multiDayDetailFinished}
                      onReplay={multiDayMapRender.replayDetail}
                      isPaused={
                        multiDayReplay.isReplaying
                          ? multiDayReplay.isPaused
                          : multiDayMapRender.isPaused
                      }
                      onTogglePause={() =>
                        multiDayReplay.isReplaying
                          ? multiDayReplay.togglePause()
                          : multiDayMapRender.togglePause()
                      }
                      onStop={() =>
                        multiDayReplay.isReplaying
                          ? multiDayReplay.stopReplay()
                          : multiDayMapRender.isDetailAnimating
                            ? multiDayMapRender.stopDetailAnimation()
                            : // Replay All and the initial reveal are the
                              // same timeline run - one stop ends either
                              multiDayMapRender.stopReplay()
                      }
                      progressPercent={
                        multiDayReplay.isReplaying
                          ? multiDayReplay.replayPercentage
                          : multiDayMapRender.isReplayingAll
                            ? multiDayMapRender.replayProgress
                            : multiDayMapRender.isDetailAnimating
                              ? multiDayMapRender.detailProgress
                              : multiDayMapRender.drawProgress
                      }
                      formattedClock={multiDayFormattedClock}
                      vehicleSpeedLabel={formatMultiDaySpeed(
                        multiDayReplay.replaySpeedKph ??
                          multiDayMapRender.detailSpeedKph
                      )}
                      isFastForward={
                        !multiDayReplay.isReplaying &&
                        multiDayMapRender.isFastForwarding
                      }
                      currentSpeed={
                        multiDayReplay.isReplaying
                          ? multiDayReplay.replaySpeed
                          : multiDayMapRender.replaySpeed
                      }
                      onSetSpeed={(speed) =>
                        multiDayReplay.isReplaying
                          ? multiDayReplay.setReplaySpeed(speed)
                          : multiDayMapRender.setReplaySpeed(speed)
                      }
                    />
                  </div>
                )}

                {/* Extra loader to be shown while map refreshes */}
                {hideMapNearRefresh && (
                  <MapLoadingAnimation
                    // bgClassName="bg-red-200"
                    bgOpacityClassName="bg-opacity-100"
                  />
                )}

                {/* Directions Panel */}
                <DirectionsPanel
                  directionsRenderer={directionsRenderer}
                  onClose={() => setDirectionsRenderer(null)}
                />

                {/* Top-left map controls - route view, event filter,
                    compare routes, overlaps - laid out in one row like the
                    multi-vehicle map, so the row's own width decides where
                    each button lands instead of hardcoded pixel offsets. */}
                {!!mapState?.mapType &&
                  // !APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(
                  //   mapState?.mapType
                  // ) &&
                  // we should hide event filters button only on streetview mode
                  mapState?.mapType !== GOOGLE_MAP_MODE_NAMES.STREETVIEW &&
                  !(mobileHelpers.viewportIsMobile && mapSearchboxOpen) && (
                    <div
                      className={`absolute flex flex-wrap gap-2 top-5 left-5 z-[60]`}
                      // z-[APP_CONFIG.MAPS.ROUTE_STYLE_FILTER_BUTTON_ZINDEX]
                    >
                      {/* Route View Filter - also drives multi-day route line style, see useMultiDayMapRender */}
                      {dataSingleVehicle?.sparseeventreporting !== 1 && (
                        <div className="relative">
                          <AdminFormFieldSubmit
                            type="button"
                            variant="secondary"
                            buttonDimensionClass={`${!!showRouteViewsPopup ? "enabled:bg-accent-blue-pale" : "enabled:bg-white"} border-0 h-10 w-full px-3`}
                            // buttonDimensionClass={`bg-accent-blue-pale border-0 h-10 w-full px-3`}
                            customWrapperClass={`shadow-md rounded-lg`}
                            label={
                              <span className="flex size-5">
                                <img
                                  src={svgIcons.LineStyleIcon}
                                  alt={"line-style"}
                                  className="flex"
                                />
                              </span>
                            }
                            titleText={t("line_style_filter")}
                            onClick={() => {
                              setShowRouteViewsPopup((state) => !state);
                            }}
                          />
                          {/* Route Views Popup */}
                          {!!showRouteViewsPopup && (
                            <div
                              className={`absolute bg-white shadow-md rounded-lg px-3 pt-3 pb-2 flex flex-col gap-3 top-12 left-0 w-56`}
                            >
                              {RouteCheckboxes}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Event Filters - also reachable for the multi-day
                          view's focused trip (multiDayDetailedTripKey),
                          reusing this same button/modal instead of building
                          a separate one */}
                      {!crashTrackEventData && (
                        <>
                          {/* only relevant once a single trip's events are on
                              the map */}
                          {(selectedTrip !== null ||
                            multiDayDetailedTripKey !== null ||
                            multiDayReplay.isReplaying) && (
                            <AdminFormFieldSubmit
                              type="button"
                              variant="secondary"
                              buttonDimensionClass="bg-white border-0 h-10 w-full px-4"
                              customWrapperClass="shadow-md rounded-lg"
                              label={t("event_filter_button")}
                              onClick={() => {
                                dispatch(
                                  setModalsData({
                                    ...modalsState,
                                    showLayerFilter: false,
                                    showVehicleFilter: false,
                                    showVehicleDetails: false,
                                    showEventFilter: true,
                                  })
                                );
                              }}
                            />
                          )}
                          {isMultiDay && (
                            <AdminFormFieldSubmit
                              type="button"
                              variant="secondary"
                              buttonDimensionClass="bg-white border-0 h-10 w-full px-4 whitespace-nowrap"
                              customWrapperClass="shadow-md rounded-lg"
                              label="Compare Routes"
                              onClick={() => setIsCompareModalOpen(true)}
                            />
                          )}

                          {/* sits with the other map actions rather than off in
                              a corner - showing where days retrace the same
                              road is an action like the rest */}
                          {isMultiDay && dateGroups.length > 0 && (
                            <button
                              type="button"
                              role="switch"
                              aria-checked={overlapsEnabled}
                              onClick={() =>
                                setOverlapsEnabled((enabled) => !enabled)
                              }
                              title={
                                overlapsEnabled
                                  ? "Hide where routes overlap"
                                  : "Show where routes overlap"
                              }
                              className={`shrink-0 rounded-lg shadow-md px-4 h-10 flex items-center gap-2.5 text-sm font-medium whitespace-nowrap hover:bg-accent-blue-paleO66 ${
                                overlapsEnabled
                                  ? "bg-accent-blue-pale text-accent-blue-dark"
                                  : "bg-white text-heading-gray888"
                              }`}
                            >
                              <span>Overlaps</span>
                              <span
                                className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                                  overlapsEnabled
                                    ? "bg-accent-blue-dark"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block size-3 rounded-full bg-white transition-transform ${
                                    overlapsEnabled
                                      ? "translate-x-3.5"
                                      : "translate-x-0.5"
                                  }`}
                                />
                              </span>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  )}

                {/* Search Box and Layer Filters */}
                {/* Code moved to `MapTopRightControls` component */}

                {/* Refresh Control Buttons */}
                {!crashTrackEventData &&
                  !showRouteViewsPopup &&
                  selectedTrip !== null &&
                  !!mapState?.mapType &&
                  // instead of hiding completely for birdseye and streetview, we will show refresh control but adjust the placement as appropriate
                  // !APP_CONFIG.MAPS.SPECIAL_VIEW_TYPES.includes(
                  //   mapState?.mapType
                  // ) &&
                  !(mobileHelpers.viewportIsMobile && mapSearchboxOpen) && (
                    <div
                      className={`absolute ${mapState?.mapType === GOOGLE_MAP_MODE_NAMES.STREETVIEW ? "top-20" : "top-16 mt-1"} ${APP_CONFIG.DEBUG.DISPLAY_VERBOSE && false ? "left-72 ms-2" : "left-5"} bg-white shadow-md rounded-lg px-3 pt-3 pb-2 flex flex-col gap-3 z-[50]`}
                      // z-[APP_CONFIG.MAPS.REFRESH_BUTTONS_ZINDEX]
                    >
                      <div
                        className={`flex flex-col gap-3${mapSearchboxOpen ? " opacity-60" : ""}`}
                      >
                        {refreshOptionsExpanded && (
                          <h4 className="flex items-center justify-between text-accent-blue-dark font-semibold">
                            {t("live_updates")}
                            <button
                              type="button"
                              className="size-7 flex items-center justify-center rounded-lg hover:bg-gray-200 focus:bg-gray-200"
                              onClick={() => setRefreshOptionsExpanded(false)}
                            >
                              <img src={svgIcons.MinimizeIcon} alt="minimize" />
                            </button>
                          </h4>
                        )}
                        <div className="flex items-center justify-between Xgap-2">
                          <div
                            className={`min-w-20 h-7 flex items-center gap-2 text-base me-2`}
                          >
                            <img
                              src={
                                liveUpdatesHeld
                                  ? svgIcons.UpdatesPaused
                                  : svgIcons.UpdatesLive
                              }
                              alt={liveUpdatesHeld ? "paused" : "live"}
                              className="flex"
                            />
                            {isPastDatesOnly
                              ? t("updates_off_status")
                              : refreshPaused
                                ? t("updates_paused_status")
                                : t("updates_live_status")}
                          </div>
                          {refreshOptionsExpanded ? (
                            // nothing to pause or resume on days that have
                            // ended, so the control goes rather than sitting
                            // there inert
                            <>
                              {isPastDatesOnly
                                ? null
                                : MemoizedRefreshPlayPauseButton}
                            </>
                          ) : (
                            <>
                              {!mapSearchboxOpen && (
                                <button
                                  type="button"
                                  className="size-7 flex items-center justify-center rounded-lg hover:bg-gray-200 focus:bg-gray-200"
                                  onClick={() =>
                                    setRefreshOptionsExpanded((state) => !state)
                                  }
                                >
                                  <img
                                    src={svgIcons.ExpandWindowIcon}
                                    alt="expand"
                                  />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                        {refreshOptionsExpanded && isPastDatesOnly && (
                          <p className="text-heading-gray888">
                            {t("updates_off_past_date")}
                          </p>
                        )}
                        {/* Refresh countdown progress bar */}
                        <RefreshCountdownBar
                          nextRefreshAtRef={nextRefreshAtRef}
                          refreshTotalMsRef={refreshTotalMsRef}
                          refreshPaused={liveUpdatesHeld}
                          userMapRefreshing={userMapRefreshing}
                          translationFn={t}
                          refreshFrequency={refreshFrequency}
                          expanded={refreshOptionsExpanded}
                        />
                      </div>
                      {refreshOptionsExpanded && (
                        <>
                          <hr className="h-1" />
                          <div className="flex items-center gap-2 -mt-2 pb-1">
                            <div className="text-base">{t("frequency")}</div>
                            <AdminFormFieldDropdown
                              label={false}
                              id="frequency"
                              name="frequency"
                              value={refreshFrequency}
                              options={
                                APP_CONFIG.MAPS.REFRESH_FREQUENCY_OPTIONS
                              }
                              onChange={(e) => {
                                setRefreshFrequency(
                                  e?.value ??
                                    APP_CONFIG.MAPS.REFRESH_FREQUENCY_OPTIONS[0]
                                      .value
                                );
                              }}
                              customWrapperClass="w-28"
                              customSelectboxClass=""
                              dimensionClass="w-full h-7"
                              menuPlacement={
                                APP_CONFIG.DEBUG.ALL ? "bottom" : "top"
                              }
                              disabled={replayInProgress || liveUpdatesHeld}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
              </>
            ) : (
              <div className="h-full w-full flex items-center justify-center relative">
                <MapLoadingAnimation
                  bgClassName="bg-gray-200"
                  bgOpacityClassName="bg-opacity-100"
                />
                {APP_CONFIG.DEBUG.MAPS && (
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 mt-8">
                    <h4 className="font-bold text-lg text-black">
                      {!mapState?.mapData ||
                      Object.keys(mapState?.mapData).length < 2
                        ? tMain("maps.receiving_data")
                        : ""}
                    </h4>
                  </div>
                )}
              </div>
            )}
            {!!vehicleId &&
              !!dataSingleVehicle &&
              mapState?.mapType !== GOOGLE_MAP_MODE_NAMES.STREETVIEW && (
                <ModalVehicleTripEventDetails
                  vehicleId={vehicleId}
                  vehicleData={dataSingleVehicle}
                  tripPointData={selectedTripPoint}
                  setSelectedTripPoint={setSelectedTripPoint}
                  handleMapTypeChange={handleMapTypeChange}
                  buzzerAlertTimestamp={buzzerAlertTimestamp}
                  buzzerAlertId={parseInt(`${buzzerAlertId}`)}
                  // NOT handleModalCloseAndReset. That one re-asserts
                  // showVehicleDetails: true whenever an event is selected,
                  // which is right when the *layer filter* closes over an
                  // event but wrong here: the popup's own hideModal has just
                  // dispatched false, and both dispatches spread the same
                  // stale modalsState in one handler, so the re-open won and
                  // the flag never went down. The popup still disappeared -
                  // it renders nothing without a tripPointData - so it looked
                  // closed while every effect keyed on the flag, the camera
                  // restore included, went on thinking it was open.
                  // Same handler the multi-vehicle map uses.
                  onEventDetailModalClose={() => {
                    setSelectedTripPoint(null);
                    multiDayMapRender.highlightEventPoint(null);
                  }}
                />
              )}

            {/* POPUPS - Event Filters / Layer Filters */}
            {/* not while comparing: the compare view mounts its own copy of
                this modal for the trips on its cards, and both instances
                read the same redux flag, so leaving this one up would open
                two stacked filters over each other */}
            {!crashTrackEventData && !isComparing && modalsState?.showEventFilter && (
              <ModalEventFilter
                statusCodes={filterableStatusCodes}
                filteredStatusCodes={filteredStatusCodes}
                handleEventFiltersChange={setFilteredStatusCodes}
                statusCodeDescriptions={inputStatusCodeDescriptions}
              />
            )}
            <ModalLayerFilters
              onMapTypeChange={handleMapTypeChange}
              onMapLayerChange={handleMapLayerChange}
              onMapCloseClick={handleModalCloseAndReset}
            />
            {(thisOrgCameraRecorderType !==
              ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX ||
              (thisOrgCameraRecorderType ===
                ACCOUNT_RECORDER_TYPE_VALUES.VISIONMAX &&
                !!vMCameraEventGetVideoDetailResponse)) && (
              <ModalCameraVideo
                data={undefined}
                {...(!!vMCameraEventGetVideoDetailResponse
                  ? {
                      vmData: vMCameraEventGetVideoDetailResponse,
                      onClose: () => {
                        setVMCameraEventGetVideoDetailResponse(null);
                      },
                    }
                  : {})}
              />
            )}
            <ModalRequestVideo
              onVideoRequestConfirm={initiateVideoRequest}
              onVideoRequestCancel={() => null}
            />

            <CompareRoutesModal
              visible={isCompareModalOpen}
              dateGroups={dateGroups}
              selectedKeys={compareTripKeys}
              maxSelectable={MAX_COMPARE_TRIPS}
              onHide={() => setIsCompareModalOpen(false)}
              onConfirm={(keys) => {
                setCompareTripKeys(keys);
                setIsComparing(true);
                setIsCompareModalOpen(false);
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ScreenMapAsset;
